import { Component, useEffect, useMemo, useRef, useState, type ErrorInfo, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceLine,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import {
  assessHousingResolutionQuality,
  buildHousingTechnicalPoints,
  buildOfficialHousingChartScopeData,
  canonicalHousingChartBars,
  chooseAutoPriceBasis,
  chooseAutoResolution,
  chooseRecommendedResolution,
  filterHousingChartRange,
  isOfficialHousingChartScope,
  loadHousingMarketChartScope,
  type HousingChartBand,
  type HousingChartPriceBasis,
  type HousingChartRange,
  type HousingChartResolution,
  type HousingChartResolutionSelection,
  type HousingChartScopeId,
  type HousingMacdPreset,
  type HousingMarketChartScopeData,
  type HousingTechnicalPoint
} from "../data/housingTechnicalChart";
import type { IndicatorSeries } from "../types";
import { recordRuntimeDiagnosticError, setRuntimeDiagnosticContext } from "../utils/runtimeDiagnostics";
import { housingCanonicalRepositoryClient } from "../data/housingCanonicalRepositoryClient.ts";
import { HOUSING_CANONICAL_REPOSITORY_CACHE_READY } from "../data/housingHtsRegistry.ts";
import type { HousingDetailedHistoryScope } from "../data/housingHistoryContract.ts";
import { buildCanonicalHousingChartScopeData, canonicalHistoryAvailableScopes, canonicalHistoryCoverage, loadCanonicalHousingHistoryPage, mergeCanonicalDailyRows, presetCanonicalViewport, shouldLoadCanonicalOlder, viewportForCanonicalDateRange, type HousingCanonicalDailyRow, type HousingCanonicalHistoryCoverage } from "../data/housingCanonicalHistoryUi.ts";

const SCOPE_LABELS: Record<HousingChartScopeId, string> = {
  national: "전국",
  capital: "수도권",
  noncapital: "비수도권",
  seoul: "서울",
  gyeonggi: "경기",
  incheon: "인천",
  gangnam3: "강남3구",
  prime4: "강남3구+용산",
  seoul_non_gangnam: "서울 비강남3구"
};
const SCOPE_ORDER: HousingChartScopeId[] = ["national", "capital", "noncapital", "seoul", "gyeonggi", "incheon", "seoul_non_gangnam", "gangnam3", "prime4"];
const RESOLUTION_OPTIONS: Array<{ id: HousingChartResolutionSelection; label: string }> = [
  { id: "auto", label: "자동" },
  { id: "tick", label: "실거래" },
  { id: "1d", label: "1일" },
  { id: "3d", label: "3일" },
  { id: "7d", label: "1주" },
  { id: "14d", label: "2주" },
  { id: "1m", label: "1개월" }
];
const RANGE_OPTIONS: HousingChartRange[] = ["1M", "3M", "6M", "1Y", "MAX"];
const PRICE_MA_KEYS = ["priceMa1", "priceMa2", "priceMa3", "priceMa4"] as const;
const VOLUME_MA_KEYS = ["volumeMa1", "volumeMa2", "volumeMa3"] as const;
const LINE_COLORS = ["#f59e0b", "#7c3aed", "#059669", "#475569"];
const UP_COLOR = "#e53935";
const DOWN_COLOR = "#2563eb";
const FLAT_COLOR = "#64748b";
const SYNC_ID = "housing-hts-chart";
const Y_AXIS_WIDTH = 58;
const CHART_MARGIN = { top: 24, right: 8, bottom: 0, left: 4 };


function StableChartViewport({ height, children }: { height: number; children: (width: number) => ReactNode }) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const commitWidth = (value: number) => {
      const next = Math.max(0, Math.floor(value));
      setWidth((current) => Math.abs(current - next) >= 1 ? next : current);
    };
    const measure = () => commitWidth(host.getBoundingClientRect().width);
    measure();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure, { passive: true });
      return () => window.removeEventListener("resize", measure);
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      commitWidth(entry?.contentRect.width ?? host.getBoundingClientRect().width);
    });
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={hostRef} style={{ width: "100%", minWidth: 0, height }}>
      {width > 0 ? children(width) : null}
    </div>
  );
}

class HousingHtsErrorBoundary extends Component<{ children: ReactNode }, { message: string | null }> {
  state = { message: null as string | null };

  static getDerivedStateFromError(error: unknown) {
    return { message: error instanceof Error ? error.message : String(error) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    recordRuntimeDiagnosticError("housing-hts", error, { componentStack: info.componentStack });
    console.error("[Housing HTS] render failure", error, info);
  }

  render() {
    if (this.state.message) {
      return (
        <section className="housing-tech-chart housing-hts">
          <div className="housing-tech-empty">
            <strong>HTS 차트 렌더링 오류</strong>
            <span>{this.state.message}</span>
            <small>다른 부동산·거시 데이터 화면은 계속 사용할 수 있습니다. 새로고침 후에도 반복되면 진단 ZIP의 frontend/runtime-errors.json을 확인합니다.</small>
          </div>
        </section>
      );
    }
    return this.props.children;
  }
}

function finite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

type HousingSeriesSegment = {
  startDate: string;
  endDate: string;
  validCount: number;
};

type HousingSeriesCoverage = {
  startDate: string | null;
  endDate: string | null;
  validCount: number;
  totalCount: number;
  segments: HousingSeriesSegment[];
};

function seriesCoverage<T>(
  rows: T[],
  dateOf: (row: T) => string | null | undefined,
  valid: (row: T) => boolean,
  continuous?: (previousDate: string, currentDate: string) => boolean,
): HousingSeriesCoverage {
  let startDate: string | null = null;
  let endDate: string | null = null;
  let validCount = 0;
  const segments: HousingSeriesSegment[] = [];
  let current: HousingSeriesSegment | null = null;
  for (const row of rows) {
    const date = dateOf(row);
    if (!date || !valid(row)) {
      if (current) {
        segments.push(current);
        current = null;
      }
      continue;
    }
    validCount += 1;
    if (startDate == null || date < startDate) startDate = date;
    if (endDate == null || date > endDate) endDate = date;
    if (current && (!continuous || continuous(current.endDate, date))) {
      current.endDate = date;
      current.validCount += 1;
    } else {
      if (current) segments.push(current);
      current = { startDate: date, endDate: date, validCount: 1 };
    }
  }
  if (current) segments.push(current);
  return { startDate, endDate, validCount, totalCount: rows.length, segments };
}

function segmentLabel(segment: HousingSeriesSegment) {
  return segment.startDate === segment.endDate ? segment.startDate : `${segment.startDate} → ${segment.endDate}`;
}

function coverageRangeLabel(coverage: HousingSeriesCoverage) {
  if (coverage.segments.length === 0) return "N/A";
  if (coverage.segments.length <= 3) return coverage.segments.map(segmentLabel).join(" · ");
  return `${segmentLabel(coverage.segments[0])} · … ${coverage.segments.length - 2}구간 … · ${segmentLabel(coverage.segments[coverage.segments.length - 1])}`;
}

function monthSegmentLabel(segment: { startMonth: string; endMonth: string }) {
  return segment.startMonth === segment.endMonth ? segment.startMonth : `${segment.startMonth} → ${segment.endMonth}`;
}

function monthSegmentsFromMonths(months: string[]) {
  const values = Array.from(new Set(months.filter((month) => /^\d{4}-\d{2}$/.test(month)))).sort();
  const segments: Array<{ startMonth: string; endMonth: string; monthCount: number }> = [];
  let current: { startMonth: string; endMonth: string; monthCount: number } | null = null;
  let previousSerial: number | null = null;
  for (const month of values) {
    const [year, monthNumber] = month.split("-").map(Number);
    const serial = year * 12 + monthNumber;
    if (current && previousSerial != null && serial === previousSerial + 1) {
      current.endMonth = month;
      current.monthCount += 1;
    } else {
      if (current) segments.push(current);
      current = { startMonth: month, endMonth: month, monthCount: 1 };
    }
    previousSerial = serial;
  }
  if (current) segments.push(current);
  return segments;
}

function monthCoverageRangeLabel(segments: Array<{ startMonth: string; endMonth: string }>) {
  return segments.length > 0 ? segments.map(monthSegmentLabel).join(" · ") : "N/A";
}

function chartDateContinuous(resolution: HousingChartResolution) {
  return (previousDate: string, currentDate: string) => {
    if (resolution === "1m") {
      const [py, pm] = previousDate.slice(0, 7).split("-").map(Number);
      const [cy, cm] = currentDate.slice(0, 7).split("-").map(Number);
      return cy * 12 + cm === py * 12 + pm + 1;
    }
    const days = Math.round((Date.parse(`${currentDate}T00:00:00Z`) - Date.parse(`${previousDate}T00:00:00Z`)) / 86_400_000);
    const expected = resolution === "14d" ? 14 : resolution === "7d" ? 7 : resolution === "3d" ? 3 : 1;
    const tolerance = resolution === "1d" ? 4 : expected + 1;
    return days > 0 && days <= tolerance;
  };
}

function fmtDate(value: unknown, resolution: HousingChartResolution) {
  const date = String(value);
  if (resolution === "1m") return date.slice(0, 7).replace("-", ".");
  return date.slice(2).replaceAll("-", ".");
}

function eok(value: number | null | undefined, digits = 1) {
  return value == null || !Number.isFinite(value) ? "N/A" : `${(value / 100_000_000).toFixed(digits)}억`;
}

function resolutionLabel(value: HousingChartResolution) {
  return value === "1d" ? "1일봉" : value === "3d" ? "3일봉" : value === "7d" ? "7일봉" : value === "14d" ? "14일봉" : "월봉";
}

function priceBasisLabel(value: Exclude<HousingChartPriceBasis, "auto">) {
  if (value === "official_index") return "공식 아파트 매매가격지수";
  if (value === "fixed_cell") return "Strict Fixed · 동일 셀 인접비교";
  if (value === "raw_median") return "Raw observed · 실제 거래 중앙";
  return "Quality-adjusted · cell 정규화";
}

function priceBasisDescription(value: Exclude<HousingChartPriceBasis, "auto">) {
  if (value === "official_index") return "한국부동산원 공식 월간지수를 기준 100으로 재표시합니다.";
  if (value === "fixed_cell") return "진단용 엄격선입니다. 인접 기간에 같은 단지×5㎡ cell이 다시 거래된 경우만 연결하고, 공통셀이 부족하면 gap을 그대로 둡니다.";
  if (value === "raw_median") return "각 봉에서 실제 체결된 주택가격 중앙값을 기준 100으로 재표시합니다. 지역·단지·평형 거래구성 변화가 그대로 섞입니다.";
  return "단지×5㎡ cell마다 retrospective 기준가격을 제거한 뒤 cell/day 상대가격을 합성합니다. 인접 봉 재거래를 요구하지 않아 연속성을 유지하면서 구성효과를 완화합니다.";
}

function pointColor(point: HousingTechnicalPoint) {
  return point.candleDirection === "up" ? UP_COLOR : point.candleDirection === "down" ? DOWN_COLOR : FLAT_COLOR;
}

function rangeCutoff(latestDate: string, range: HousingChartRange) {
  if (range === "MAX") return null;
  const latest = new Date(`${latestDate}T00:00:00Z`);
  const months = range === "1M" ? 1 : range === "3M" ? 3 : range === "6M" ? 6 : 12;
  return new Date(Date.UTC(latest.getUTCFullYear(), latest.getUTCMonth() - months, latest.getUTCDate()));
}

function inRange(date: string, latestDate: string, range: HousingChartRange) {
  const cutoff = rangeCutoff(latestDate, range);
  return cutoff == null || new Date(`${date}T00:00:00Z`) >= cutoff;
}

function fixedGapReason(point: HousingTechnicalPoint | null | undefined, guard: HousingMarketChartScopeData["fixedCellGuard"] | null | undefined) {
  if (!point || point.fixedCellStatus === "ready") return null;
  const minCells = guard?.minimumMatchedCells ?? 5;
  const minTransactions = guard?.minimumMatchedTransactionsEachBar ?? 10;
  const minCoverage = guard?.minimumOriginPreviousTransactionCoveragePct ?? guard?.minimumTransactionCoveragePctEachBar ?? 30;
  const minSurvival = guard?.minimumOriginCellSurvivalPct ?? guard?.minimumTransactionCoveragePctEachBar ?? 30;
  const reasons: string[] = [];
  if ((point.matchedCellCount ?? 0) < minCells) reasons.push(`공통셀 ${point.matchedCellCount ?? 0}개 < ${minCells}개`);
  if ((point.matchedPreviousTransactionCount ?? 0) < minTransactions || (point.matchedCurrentTransactionCount ?? 0) < minTransactions) reasons.push(`공통셀 거래 ${Math.min(point.matchedPreviousTransactionCount ?? 0, point.matchedCurrentTransactionCount ?? 0)}건 < ${minTransactions}건`);
  if ((point.originPreviousTransactionCoveragePct ?? 0) < minCoverage) reasons.push(`cohort 포착 ${(point.originPreviousTransactionCoveragePct ?? 0).toFixed(0)}% < ${minCoverage}%`);
  if ((point.originCellSurvivalPct ?? 0) < minSurvival) reasons.push(`셀 생존 ${(point.originCellSurvivalPct ?? 0).toFixed(0)}% < ${minSurvival}%`);
  return reasons.slice(0, 2).join(" · ") || "공통셀 guard 미충족";
}

function qualityTone(value: number) {
  return value >= 0.8 ? "good" : value >= 0.55 ? "warn" : "thin";
}

function adaptiveBarSize(chartWidth: number, pointCount: number, max = 14) {
  const drawable = Math.max(80, chartWidth - Y_AXIS_WIDTH - CHART_MARGIN.left - CHART_MARGIN.right);
  return Math.max(1, Math.min(max, Math.floor((drawable / Math.max(1, pointCount)) * 0.72)));
}

type HousingChartViewportWindow = { start: number; end: number };

const MIN_VIEWPORT_BARS = 8;

function clampNumber(value: number, minimum: number, maximum: number) {
  return Math.min(maximum, Math.max(minimum, value));
}

function normalizeViewportWindow(window: HousingChartViewportWindow | null, length: number): HousingChartViewportWindow {
  if (length <= 0) return { start: 0, end: -1 };
  if (!window) return { start: 0, end: length - 1 };
  const end = clampNumber(Math.round(window.end), 0, length - 1);
  const start = clampNumber(Math.round(window.start), 0, end);
  return { start, end };
}

function zoomViewportWindow(window: HousingChartViewportWindow, length: number, factor: number, anchorRatio = 0.5): HousingChartViewportWindow {
  if (length <= 0) return { start: 0, end: -1 };
  const normalized = normalizeViewportWindow(window, length);
  const currentSize = normalized.end - normalized.start + 1;
  const minimumSize = Math.min(length, MIN_VIEWPORT_BARS);
  const nextSize = clampNumber(Math.round(currentSize * factor), minimumSize, length);
  if (nextSize === currentSize) return normalized;
  const anchor = clampNumber(anchorRatio, 0, 1);
  const anchorIndex = normalized.start + anchor * Math.max(0, currentSize - 1);
  let start = Math.round(anchorIndex - anchor * Math.max(0, nextSize - 1));
  start = clampNumber(start, 0, Math.max(0, length - nextSize));
  return { start, end: start + nextSize - 1 };
}

function panViewportWindow(window: HousingChartViewportWindow, length: number, shiftBars: number): HousingChartViewportWindow {
  if (length <= 0) return { start: 0, end: -1 };
  const normalized = normalizeViewportWindow(window, length);
  const size = normalized.end - normalized.start + 1;
  if (size >= length) return normalized;
  const start = clampNumber(normalized.start + Math.round(shiftBars), 0, length - size);
  return { start, end: start + size - 1 };
}

function hasFixedCellHistory(data: HousingMarketChartScopeData, bandId: string) {
  return (["7d", "14d", "1m"] as HousingChartResolution[]).some((candidate) =>
    (data.bars[candidate]?.[bandId] ?? []).some((row) => row.fixedCellStatus === "ready" && finite(row.fixedCellIndex))
  );
}

function signed(value: number | null | undefined, digits = 2) {
  if (!finite(value)) return "N/A";
  return `${value > 0 ? "+" : ""}${value.toFixed(digits)}`;
}

function CandleShape(props: any) {
  const { x = 0, y = 0, width = 0, height = 0, payload } = props;
  const low = payload?.candleLow;
  const high = payload?.candleHigh;
  const open = payload?.candleOpen;
  const close = payload?.candleClose;
  if (![low, high, open, close].every(finite)) return null;
  const span = Math.max(high - low, 1e-9);
  const toY = (value: number) => y + ((high - value) / span) * Math.max(height, 1);
  const openY = toY(open);
  const closeY = toY(close);
  const centerX = x + width / 2;
  const bodyWidth = Math.max(1, width * 0.82);
  const bodyX = centerX - bodyWidth / 2;
  const bodyY = Math.min(openY, closeY);
  const bodyHeight = Math.max(2.2, Math.abs(closeY - openY));
  const color = payload.candleDirection === "up" ? UP_COLOR : payload.candleDirection === "down" ? DOWN_COLOR : FLAT_COLOR;
  return (
    <g>
      <line x1={centerX} x2={centerX} y1={y} y2={y + Math.max(height, 1)} stroke={color} strokeWidth={1} />
      <rect x={bodyX} y={bodyY} width={bodyWidth} height={bodyHeight} fill={color} stroke={color} rx={0.5} />
    </g>
  );
}

function updateHover(setter: (point: HousingTechnicalPoint | null) => void) {
  return (state: any) => {
    const point = state?.activePayload?.[0]?.payload as HousingTechnicalPoint | undefined;
    if (point) setter(point);
  };
}

function safeExportName(value: string) {
  return value.replace(/[^0-9A-Za-z가-힣._-]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 120);
}

function visibleCssColor(value: string) {
  if (!value || value === "transparent") return false;
  return !/^rgba\([^)]*,\s*0(?:\.0+)?\s*\)$/i.test(value.trim());
}

function cssPx(value: string | null | undefined) {
  const parsed = Number.parseFloat(value ?? "0");
  return Number.isFinite(parsed) ? parsed : 0;
}

function exportHidden(element: Element) {
  return Boolean(element.closest('[data-export-hide="true"]'));
}

function rectInsideRoot(element: Element, rootRect: DOMRect) {
  const rect = element.getBoundingClientRect();
  return {
    x: rect.left - rootRect.left,
    y: rect.top - rootRect.top,
    width: rect.width,
    height: rect.height
  };
}

function paintHtmlBox(ctx: CanvasRenderingContext2D, element: HTMLElement, rootRect: DOMRect) {
  if (exportHidden(element)) return;
  const style = getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden" || Number(style.opacity) === 0) return;
  const rect = rectInsideRoot(element, rootRect);
  if (rect.width <= 0 || rect.height <= 0) return;

  if (visibleCssColor(style.backgroundColor)) {
    ctx.fillStyle = style.backgroundColor;
    ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
  }

  const borders = [
    ["top", cssPx(style.borderTopWidth), style.borderTopColor],
    ["right", cssPx(style.borderRightWidth), style.borderRightColor],
    ["bottom", cssPx(style.borderBottomWidth), style.borderBottomColor],
    ["left", cssPx(style.borderLeftWidth), style.borderLeftColor]
  ] as const;
  for (const [side, width, color] of borders) {
    if (width <= 0 || !visibleCssColor(color)) continue;
    ctx.fillStyle = color;
    if (side === "top") ctx.fillRect(rect.x, rect.y, rect.width, width);
    if (side === "right") ctx.fillRect(rect.x + rect.width - width, rect.y, width, rect.height);
    if (side === "bottom") ctx.fillRect(rect.x, rect.y + rect.height - width, rect.width, width);
    if (side === "left") ctx.fillRect(rect.x, rect.y, width, rect.height);
  }
}

function transformedExportText(value: string, transform: string) {
  if (transform === "uppercase") return value.toUpperCase();
  if (transform === "lowercase") return value.toLowerCase();
  if (transform === "capitalize") return value.replace(/(^|\s)(\S)/g, (match) => match.toUpperCase());
  return value;
}

function drawTextRange(
  ctx: CanvasRenderingContext2D,
  node: Text,
  start: number,
  end: number,
  value: string,
  style: CSSStyleDeclaration,
  rootRect: DOMRect
) {
  const range = document.createRange();
  range.setStart(node, start);
  range.setEnd(node, end);
  const rects = Array.from(range.getClientRects()).filter((rect) => rect.width > 0 && rect.height > 0);
  range.detach?.();
  if (rects.length === 0) return;
  if (rects.length > 1 && end - start > 1) {
    for (let offset = start; offset < end; offset += 1) {
      const char = node.data.slice(offset, offset + 1);
      if (!char || /\s/.test(char)) continue;
      drawTextRange(ctx, node, offset, offset + 1, char, style, rootRect);
    }
    return;
  }
  const rect = rects[0];
  const text = transformedExportText(value, style.textTransform);
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(text, rect.left - rootRect.left, rect.top - rootRect.top + rect.height / 2);
}

function drawOwnHtmlText(ctx: CanvasRenderingContext2D, element: HTMLElement, rootRect: DOMRect) {
  if (exportHidden(element)) return;
  const style = getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden" || Number(style.opacity) === 0) return;
  const fontSize = cssPx(style.fontSize) || 12;
  const fontStyle = style.fontStyle && style.fontStyle !== "normal" ? `${style.fontStyle} ` : "";
  const fontWeight = style.fontWeight && style.fontWeight !== "normal" ? `${style.fontWeight} ` : "";
  ctx.font = `${fontStyle}${fontWeight}${fontSize}px ${style.fontFamily || "sans-serif"}`;
  ctx.fillStyle = visibleCssColor(style.color) ? style.color : "#111827";

  for (const child of Array.from(element.childNodes)) {
    if (child.nodeType !== Node.TEXT_NODE) continue;
    const node = child as Text;
    const raw = node.data;
    if (!raw || !/\S/.test(raw)) continue;
    const tokenPattern = /\S+/g;
    let match: RegExpExecArray | null;
    while ((match = tokenPattern.exec(raw)) !== null) {
      drawTextRange(ctx, node, match.index, match.index + match[0].length, match[0], style, rootRect);
    }
  }
}

async function drawInlineSvg(ctx: CanvasRenderingContext2D, svg: SVGSVGElement, rootRect: DOMRect) {
  if (exportHidden(svg)) return;
  const rect = rectInsideRoot(svg, rootRect);
  if (rect.width <= 0 || rect.height <= 0) return;
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("width", String(Math.max(1, rect.width)));
  clone.setAttribute("height", String(Math.max(1, rect.height)));
  clone.querySelectorAll("foreignObject").forEach((node) => node.remove());
  clone.querySelectorAll("image").forEach((node) => {
    const href = node.getAttribute("href") ?? node.getAttributeNS("http://www.w3.org/1999/xlink", "href") ?? "";
    if (href && !href.startsWith("data:") && !href.startsWith("blob:")) node.remove();
  });
  const inherited = getComputedStyle(svg);
  clone.style.fontFamily = inherited.fontFamily;
  clone.style.color = inherited.color;

  const blob = new Blob([new XMLSerializer().serializeToString(clone)], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("차트 SVG 렌더링 실패"));
      image.src = url;
    });
    ctx.drawImage(image, rect.x, rect.y, rect.width, rect.height);
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function exportElementPng(element: HTMLElement, filename: string) {
  try { await document.fonts?.ready; } catch { /* best effort */ }
  const rootRect = element.getBoundingClientRect();
  const includedSections = Array.from(element.querySelectorAll<HTMLElement>("[data-export-section]"))
    .filter((node) => !exportHidden(node) && getComputedStyle(node).display !== "none")
    .map((node) => node.dataset.exportSection ?? "unknown");
  const width = Math.max(element.scrollWidth, Math.ceil(rootRect.width), 900);
  const height = Math.max(element.scrollHeight, Math.ceil(rootRect.height), 500);
  const scale = Math.min(2, Math.max(1, window.devicePixelRatio || 1));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("PNG canvas 생성 실패");
  ctx.scale(scale, scale);
  const background = getComputedStyle(element).backgroundColor;
  ctx.fillStyle = visibleCssColor(background) ? background : "#ffffff";
  ctx.fillRect(0, 0, width, height);

  const htmlElements = [element, ...Array.from(element.querySelectorAll<HTMLElement>("*:not(svg):not(svg *)"))];
  for (const node of htmlElements) paintHtmlBox(ctx, node, rootRect);
  for (const svg of Array.from(element.querySelectorAll<SVGSVGElement>("svg"))) await drawInlineSvg(ctx, svg, rootRect);
  for (const node of htmlElements) drawOwnHtmlText(ctx, node, rootRect);

  const png = await new Promise<Blob>((resolve, reject) => {
    try {
      canvas.toBlob((value) => value ? resolve(value) : reject(new Error("PNG 변환 실패")), "image/png");
    } catch (error) {
      reject(error);
    }
  });
  const href = URL.createObjectURL(png);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 30_000);
  return { width, height, includedSections: Array.from(new Set(includedSections)) };
}

function HousingMarketTechnicalChartInner({ officialHousingPrices = [] }: { officialHousingPrices?: IndicatorSeries[] }) {
  const [scope, setScope] = useState<HousingChartScopeId>("gangnam3");
  const [bandId, setBandId] = useState("all");
  const [resolutionSelection, setResolutionSelection] = useState<HousingChartResolutionSelection>("auto");
  const [priceBasisSelection, setPriceBasisSelection] = useState<HousingChartPriceBasis>("auto");
  const [range, setRange] = useState<HousingChartRange>("1Y");
  const [macdPreset, setMacdPreset] = useState<HousingMacdPreset>("standard");
  const [showPriceMa, setShowPriceMa] = useState(true);
  const [showQualityComparison, setShowQualityComparison] = useState(true);
  const [showRawComparison, setShowRawComparison] = useState(false);
  const [showFixedComparison, setShowFixedComparison] = useState(true);
  const [subIndicators, setSubIndicators] = useState({ volume: true, macd: true, rsi: false });
  const [showIndicatorMenu, setShowIndicatorMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [hoverPoint, setHoverPoint] = useState<HousingTechnicalPoint | null>(null);
  const [data, setData] = useState<HousingMarketChartScopeData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [basisNotice, setBasisNotice] = useState<string | null>(null);
  const [viewportWindow, setViewportWindow] = useState<HousingChartViewportWindow | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [canonicalSourceGenerationId,setCanonicalSourceGenerationId]=useState<string|null>(null);
  const [canonicalRows,setCanonicalRows]=useState<HousingCanonicalDailyRow[]>([]);
  const [canonicalCoverage,setCanonicalCoverage]=useState<HousingCanonicalHistoryCoverage|null>(null);
  const [canonicalNextOffset,setCanonicalNextOffset]=useState(0);
  const [canonicalDone,setCanonicalDone]=useState(true);
  const [canonicalPaging,setCanonicalPaging]=useState(false);
  const canonicalPagingRef=useRef(false);
  const canonicalWarmPrefetched=useRef(false);
  const canonicalPreserveRange=useRef<{startDate:string;endDate:string}|null>(null);
  const canonicalVisibleRange=useRef<{startDate:string;endDate:string}|null>(null);
  const previousCanonicalResolution=useRef<HousingChartResolution|null>(null);
  const exportRef = useRef<HTMLElement | null>(null);
  const interactionSurfaceRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ pointerId: number; pointerType: string; startX: number; startY: number; width: number; window: HousingChartViewportWindow; active: boolean } | null>(null);
  const officialScope = isOfficialHousingChartScope(scope);
  const tickMode = !officialScope && resolutionSelection === "tick";
  const canonicalMode = !officialScope && !tickMode;

  useEffect(() => {
    if (canonicalMode) { setLoading(false); return; }
    setError(null);
    setData(null);
    setHoverPoint(null);
    const controller = new AbortController();
    setLoading(true);
    loadHousingMarketChartScope(scope, controller.signal)
      .then((result) => setData(result))
      .catch((reason) => {
        if (controller.signal.aborted) return;
        if (isOfficialHousingChartScope(scope)) {
          const fallback = buildOfficialHousingChartScopeData(scope, officialHousingPrices);
          if (fallback) { setData(fallback); return; }
        }
        setError(reason instanceof Error ? reason.message : String(reason));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [scope, officialHousingPrices, canonicalMode]);


  useEffect(() => {
    if (!canonicalMode) return;
    const controller=new AbortController();
    setError(null);setData(null);setHoverPoint(null);setViewportWindow(null);setCanonicalRows([]);setCanonicalCoverage(null);setCanonicalSourceGenerationId(null);setCanonicalNextOffset(0);setCanonicalDone(true);canonicalWarmPrefetched.current=false;canonicalPagingRef.current=false;setCanonicalPaging(false);
    if (!HOUSING_CANONICAL_REPOSITORY_CACHE_READY){setError("canonical_repository_cache_not_ready");return()=>controller.abort();}
    setLoading(true);
    housingCanonicalRepositoryClient.currentReference(controller.signal).then(async reference=>{
      const available=canonicalHistoryAvailableScopes(reference),canonicalScope=scope as HousingDetailedHistoryScope;
      if(!available.includes(canonicalScope))throw new Error("canonical_scope_not_available");
      const sourceGenerationId=String(reference.sourceGenerationId),coverage=canonicalHistoryCoverage(reference,canonicalScope);
      const page=await loadCanonicalHousingHistoryPage(housingCanonicalRepositoryClient,{sourceGenerationId,scope:canonicalScope,bandId,offset:0},controller.signal);
      if(controller.signal.aborted)return;
      setCanonicalSourceGenerationId(sourceGenerationId);setCanonicalCoverage(coverage);setCanonicalRows(page.rows);setCanonicalNextOffset(page.nextOffset);setCanonicalDone(page.done);
    }).catch(reason=>{if(!controller.signal.aborted)setError(reason instanceof Error?reason.message:String(reason));}).finally(()=>{if(!controller.signal.aborted)setLoading(false);});
    return()=>controller.abort();
  },[canonicalMode,scope,bandId]);

  useEffect(()=>{
    if(!canonicalMode||!canonicalSourceGenerationId||!canonicalCoverage||isOfficialHousingChartScope(scope))return;
    setData(buildCanonicalHousingChartScopeData({sourceGenerationId:canonicalSourceGenerationId,scope:scope as HousingDetailedHistoryScope,bandId,rows:canonicalRows,coverage:canonicalCoverage}));
  },[canonicalMode,canonicalSourceGenerationId,canonicalCoverage,canonicalRows,scope,bandId]);

  useEffect(() => {
    if (data && !data.bands.some((band) => band.id === bandId)) setBandId("all");
  }, [data, bandId]);

  useEffect(() => {
    if (officialScope || !data || data.scope !== scope || data.dataKind === "official_index" || priceBasisSelection !== "fixed_cell") return;
    if (!hasFixedCellHistory(data, bandId)) {
      setPriceBasisSelection("auto");
      setBasisNotice("선택한 지역·가격대에는 Strict Fixed 가격봉이 없어 AUTO(Quality-adjusted)로 전환했습니다.");
    }
  }, [data, scope, bandId, officialScope, priceBasisSelection]);

  const recommendedResolution = useMemo<HousingChartResolution | null>(() => {
    if (officialScope) return "1m";
    if (!data || data.scope !== scope) return null;
    return chooseRecommendedResolution(scope, bandId, data, priceBasisSelection);
  }, [data, scope, bandId, priceBasisSelection, officialScope, canonicalMode]);
  const resolution = useMemo<HousingChartResolution>(() => {
    if (officialScope) return "1m";
    if (!data) return "1m";
    if (resolutionSelection === "auto" || resolutionSelection === "tick") return chooseAutoResolution(scope, bandId, data, tickMode ? "median_proxy" : priceBasisSelection);
    return resolutionSelection;
  }, [data, resolutionSelection, scope, bandId, officialScope, priceBasisSelection, tickMode, canonicalMode]);

  const sourceBars = useMemo(() => data?.bars[resolution]?.[bandId] ?? [], [data, resolution, bandId]);
  const rawBars = useMemo(() => data ? canonicalHousingChartBars(data, resolution, bandId) : [], [data, resolution, bandId]);
  const resolutionQuality = useMemo(() => assessHousingResolutionQuality(resolution, rawBars), [resolution, rawBars]);
  const priceBasis = useMemo<Exclude<HousingChartPriceBasis, "auto">>(() => {
    if (officialScope) return "official_index";
    if (priceBasisSelection !== "auto") return priceBasisSelection;
    return chooseAutoPriceBasis(resolution, rawBars);
  }, [priceBasisSelection, resolution, rawBars, officialScope, canonicalMode]);
  const { points, config } = useMemo(() => buildHousingTechnicalPoints(rawBars, resolution, priceBasis, macdPreset), [rawBars, resolution, priceBasis, macdPreset]);
  const rangePoints = useMemo(() => canonicalMode ? points : filterHousingChartRange(points, range), [points, range, canonicalMode]);
  const canonicalPreset = useMemo(() => canonicalMode ? presetCanonicalViewport(rangePoints.length,range,resolution) : null,[canonicalMode,rangePoints.length,range,resolution]);
  const normalizedViewport = useMemo(() => normalizeViewportWindow(viewportWindow ?? canonicalPreset, rangePoints.length), [viewportWindow,canonicalPreset,rangePoints.length]);
  const visiblePoints = useMemo(() => normalizedViewport.end >= normalizedViewport.start ? rangePoints.slice(normalizedViewport.start, normalizedViewport.end + 1) : [], [rangePoints, normalizedViewport.start, normalizedViewport.end]);
  const viewportSpan = normalizedViewport.end >= normalizedViewport.start ? normalizedViewport.end - normalizedViewport.start + 1 : 0;
  const viewportZoomed = rangePoints.length > 0 && viewportSpan < rangePoints.length;
  const viewportAtLatest = rangePoints.length === 0 || normalizedViewport.end === rangePoints.length - 1;
  const viewportStartDate = visiblePoints[0]?.date ?? null;
  const viewportEndDate = visiblePoints.at(-1)?.date ?? null;
  const latestSourceDate = rawBars.at(-1)?.endDate ?? null;
  useEffect(() => {
    if (canonicalMode) return;
    setViewportWindow(null);
    setIsPanning(false);
    dragRef.current = null;
  }, [scope, bandId, resolution, priceBasis, range, rawBars, canonicalMode]);
  useEffect(() => {
    if (hoverPoint && !visiblePoints.some((point) => point.date === hoverPoint.date)) setHoverPoint(null);
  }, [hoverPoint, visiblePoints]);
  const visibleSourceBars = useMemo(() => canonicalMode ? rawBars : (latestSourceDate ? rawBars.filter((bar) => inRange(bar.endDate, latestSourceDate, range)) : rawBars), [rawBars, range, latestSourceDate, canonicalMode]);
  const orderedTicks = useMemo(() => officialScope ? [] : [...(data?.ticksByBand[bandId] ?? [])].sort((a, b) => b.date.localeCompare(a.date)), [data, bandId, officialScope]);
  const rangeTicks = useMemo(() => {
    if (!orderedTicks.length) return [];
    const latestDate = orderedTicks[0].date;
    return orderedTicks.filter((tick) => inRange(tick.date, latestDate, range));
  }, [orderedTicks, range]);
  const ticks = useMemo(() => rangeTicks.slice(0, 300), [rangeTicks]);
  const tickCapApplied = rangeTicks.length > ticks.length;
  const tickPlot = useMemo(() => [...ticks].sort((a, b) => a.date.localeCompare(b.date)).map((tick, i) => ({ ...tick, time: Date.parse(`${tick.date}T00:00:00Z`), tick: i + 1, priceEok: tick.priceKrw / 100_000_000 })), [ticks]);
  const latestTick = ticks[0] ?? orderedTicks[0] ?? null;
  const latestDisplayPoint = visiblePoints.at(-1) ?? null;
  const rangeHasPricePoint = rangePoints.some((point) => finite(point.candleClose));
  const latestPricePoint = useMemo(() => [...visiblePoints].reverse().find((point) => finite(point.candleClose)) ?? null, [visiblePoints]);
  const viewportHasPricePoint = latestPricePoint != null;
  const activePoint = hoverPoint ?? latestPricePoint;
  const latestReportedDate = !officialScope ? (orderedTicks[0]?.date ?? sourceBars.at(-1)?.lastObservedDate ?? null) : null;
  const basisLagDays = viewportAtLatest && latestReportedDate && latestPricePoint?.date
    ? Math.max(0, Math.round((Date.parse(`${latestReportedDate}T00:00:00Z`) - Date.parse(`${latestPricePoint.date}T00:00:00Z`)) / 86_400_000))
    : 0;
  const pendingBarsAfterLatest = viewportAtLatest && latestPricePoint ? visiblePoints.filter((point) => point.date > latestPricePoint.date && !finite(point.candleClose)).length : 0;
  const fixedEvaluationBars = visibleSourceBars.filter((row) => !row.provisional && resolution !== "1d" && resolution !== "3d");
  const fixedReadyShare = fixedEvaluationBars.length ? fixedEvaluationBars.filter((row) => row.fixedCellStatus === "ready" && finite(row.fixedCellIndex)).length / fixedEvaluationBars.length : 0;
  const qualityAdjustedReadyShare = visibleSourceBars.length ? visibleSourceBars.filter((row) => row.marketIndexStatus === "ready").length / visibleSourceBars.length : 0;
  const rawReadyShare = visibleSourceBars.length ? visibleSourceBars.filter((row) => finite(row.medianPriceProxyKrw) && (row.medianPriceProxyKrw as number) > 0 && row.transactionCount > 0).length / visibleSourceBars.length : 0;
  const lowSampleShare = visibleSourceBars.length ? visibleSourceBars.filter((row) => row.sampleStatus !== "ready").length / visibleSourceBars.length : 0;
  const hasAnyFixed = !officialScope && data?.scope === scope ? hasFixedCellHistory(data, bandId) : false;
  const canFixed = !officialScope && resolution !== "1d" && resolution !== "3d" && rawBars.some((row) => row.fixedCellStatus === "ready" && finite(row.fixedCellIndex));
  const fixedGapPoints = priceBasis === "fixed_cell" ? visiblePoints.filter((point) => point.fixedCellStatus !== "ready" || !finite(point.fixedCellIndex)) : [];
  const fixedSegmentStarts = useMemo(() => {
    if (priceBasis !== "fixed_cell") return [] as Array<{ date: string; id: string }>;
    const starts: Array<{ date: string; id: string }> = [];
    let previous: string | null = null;
    for (const point of visiblePoints) {
      const id = point.fixedCellStatus === "ready" ? point.fixedCellSegmentId ?? null : null;
      if (!id) { previous = null; continue; }
      if (id !== previous) starts.push({ date: point.date, id });
      previous = id;
    }
    return starts;
  }, [visiblePoints, priceBasis]);
  const fixedResetCount = Math.max(0, fixedSegmentStarts.length - 1);
  const fixedLatestGapReason = priceBasis === "fixed_cell" && latestDisplayPoint && !finite(latestDisplayPoint.candleClose) ? fixedGapReason(latestDisplayPoint, data?.fixedCellGuard) : null;
  const activeFixedGapReason = priceBasis === "fixed_cell" && activePoint && !finite(activePoint.candleClose) ? fixedGapReason(activePoint, data?.fixedCellGuard) : null;
  const qualityComparisonAvailable = !officialScope && priceBasis !== "median_proxy" && visiblePoints.some((point) => finite(point.qualityAdjustedComparisonIndex));
  const rawComparisonAvailable = !officialScope && priceBasis !== "raw_median" && visiblePoints.some((point) => finite(point.rawObservedComparisonIndex));
  const fixedComparisonAvailable = !officialScope && priceBasis !== "fixed_cell" && canFixed && visiblePoints.some((point) => finite(point.fixedCellIndex));
  const showQualityOverlay = showQualityComparison && qualityComparisonAvailable;
  const showRawOverlay = showRawComparison && rawComparisonAvailable;
  const showFixedOverlay = showFixedComparison && fixedComparisonAvailable;
  const currentSegmentLength = useMemo(() => {
    let lastIndex = -1;
    for (let i = points.length - 1; i >= 0; i -= 1) {
      if (finite(points[i].candleClose)) {
        lastIndex = i;
        break;
      }
    }
    if (lastIndex < 0) return 0;
    const segment = priceBasis === "fixed_cell"
      ? points[lastIndex].fixedCellSegmentId ?? null
      : priceBasis === "median_proxy"
        ? points[lastIndex].marketIndexSegmentId ?? "market"
        : "market";
    if (segment === "market") return lastIndex + 1;
    let count = 0;
    for (let i = lastIndex; i >= 0; i -= 1) {
      const point = points[i];
      const pointSegment = priceBasis === "fixed_cell" ? point.fixedCellSegmentId ?? null : point.marketIndexSegmentId ?? "market";
      if (!finite(point.candleClose) || pointSegment !== segment) break;
      count += 1;
    }
    return count;
  }, [points, priceBasis]);
  const macdRequiredBars = config.macdSlow + config.macdSignal - 1;
  const longestMa = Math.max(...config.priceMaPeriods);
  const sourceMonthSegments = useMemo(() => {
    const declared = data?.coverage.availableMonthSegments ?? [];
    if (declared.length > 0) return declared;
    return monthSegmentsFromMonths(data?.coverage.completeMonths ?? []);
  }, [data]);
  const sourceCompleteMonthCount = data?.coverage.availableCompleteMonthCount ?? sourceMonthSegments.reduce((sum, segment) => sum + segment.monthCount, 0);
  const coverageContinuity = useMemo(() => chartDateContinuous(resolution), [resolution]);
  const tapeSeriesCoverage = useMemo(() => seriesCoverage(orderedTicks, (tick) => tick.date, (tick) => finite(tick.priceKrw) && tick.priceKrw > 0), [orderedTicks]);
  const qaSeriesCoverage = useMemo(() => seriesCoverage(rawBars, (bar) => bar.endDate, (bar) => bar.marketIndexStatus === "ready" && finite(bar.marketIndexClose), coverageContinuity), [rawBars, coverageContinuity]);
  const rawSeriesCoverage = useMemo(() => seriesCoverage(rawBars, (bar) => bar.endDate, (bar) => finite(bar.medianPriceProxyKrw) && (bar.medianPriceProxyKrw as number) > 0 && bar.transactionCount > 0, coverageContinuity), [rawBars, coverageContinuity]);
  const strictSeriesCoverage = useMemo(() => seriesCoverage(rawBars, (bar) => bar.endDate, (bar) => bar.fixedCellStatus === "ready" && finite(bar.fixedCellIndex), coverageContinuity), [rawBars, coverageContinuity]);
  const officialSeriesCoverage = useMemo(() => seriesCoverage(points, (point) => point.date, (point) => finite(point.candleClose), coverageContinuity), [points, coverageContinuity]);
  const volumeSeriesCoverage = useMemo(() => seriesCoverage(points, (point) => point.date, (point) => point.volume > 0, coverageContinuity), [points, coverageContinuity]);
  const priceMaSeriesCoverage = useMemo(() => config.priceMaPeriods.map((period, index) => {
    const key = PRICE_MA_KEYS[index];
    return { period, coverage: key ? seriesCoverage(points, (point) => point.date, (point) => finite(point[key]), coverageContinuity) : { startDate: null, endDate: null, validCount: 0, totalCount: points.length, segments: [] } };
  }), [points, config.priceMaPeriods, coverageContinuity]);
  const volumeMaSeriesCoverage = useMemo(() => config.volumeMaPeriods.map((period, index) => {
    const key = VOLUME_MA_KEYS[index];
    return { period, coverage: key ? seriesCoverage(points, (point) => point.date, (point) => finite(point[key]), coverageContinuity) : { startDate: null, endDate: null, validCount: 0, totalCount: points.length, segments: [] } };
  }), [points, config.volumeMaPeriods, coverageContinuity]);
  const macdSeriesCoverage = useMemo(() => seriesCoverage(points, (point) => point.date, (point) => finite(point.macd) && finite(point.macdSignal) && finite(point.macdOsc), coverageContinuity), [points, coverageContinuity]);
  const rsiSeriesCoverage = useMemo(() => seriesCoverage(points, (point) => point.date, (point) => finite(point.rsi), coverageContinuity), [points, coverageContinuity]);
  const macdHasVisibleValues = visiblePoints.some((point) => finite(point.macd) || finite(point.macdSignal) || finite(point.macdOsc));
  const rsiHasVisibleValues = visiblePoints.some((point) => finite(point.rsi));
  const basisQualityValue = priceBasis === "fixed_cell"
    ? Math.min(resolutionQuality.fixedReadyShare, resolutionQuality.fixedMatureReadyShare)
    : priceBasis === "raw_median"
      ? resolutionQuality.rawReadyShare
      : resolutionQuality.observedReadyShare;
  const basisQualityClass = priceBasis === "official_index" ? "good" : qualityTone(basisQualityValue);
  const selectedBasisUsable = priceBasis === "official_index"
    || (priceBasis === "fixed_cell" ? resolutionQuality.fixedUsable : priceBasis === "raw_median" ? resolutionQuality.rawUsable : resolutionQuality.observedUsable);
  const autoFallbackUsed = !officialScope && resolutionSelection === "auto" && !selectedBasisUsable;

  const bandOptions: HousingChartBand[] = data?.bands ?? [
    { id: "all", label: "전체" },
    { id: "m1_le_15", label: "15억원 이하" },
    { id: "m2_15_30", label: "15억 초과~30억 미만" },
    { id: "m3_30_50", label: "30억 이상~50억 미만" },
    { id: "m4_ge_50", label: "50억원 이상" }
  ];
  const bandLabel = bandOptions.find((band) => band.id === bandId)?.label ?? bandId;
  const chartMouseMove = updateHover(setHoverPoint);

  const loadCanonicalOlder=async(preserveView=true)=>{
    if(!canonicalMode||!canonicalSourceGenerationId||!canonicalCoverage||canonicalDone||canonicalPagingRef.current||isOfficialHousingChartScope(scope))return;
    if(preserveView&&visiblePoints.length)canonicalPreserveRange.current={startDate:visiblePoints[0].date,endDate:visiblePoints.at(-1)!.date};
    canonicalPagingRef.current=true;setCanonicalPaging(true);
    try{
      const page=await loadCanonicalHousingHistoryPage(housingCanonicalRepositoryClient,{sourceGenerationId:canonicalSourceGenerationId,scope:scope as HousingDetailedHistoryScope,bandId,offset:canonicalNextOffset});
      setCanonicalRows(current=>mergeCanonicalDailyRows(current,page.rows));setCanonicalNextOffset(page.nextOffset);setCanonicalDone(page.done);
    }catch(reason){setError(reason instanceof Error?reason.message:String(reason));}
    finally{canonicalPagingRef.current=false;setCanonicalPaging(false);}
  };
  const maybeLoadCanonicalOlder=(candidateStart:number,currentSpan:number)=>{
    if(!canonicalMode||!shouldLoadCanonicalOlder(candidateStart,currentSpan,canonicalDone,canonicalPagingRef.current))return;
    void loadCanonicalOlder();
  };
  useEffect(()=>{
    if(!canonicalMode||!canonicalRows.length||canonicalDone||canonicalPaging||canonicalWarmPrefetched.current)return;
    canonicalWarmPrefetched.current=true;const timer=window.setTimeout(()=>{void loadCanonicalOlder();},250);return()=>window.clearTimeout(timer);
  },[canonicalMode,canonicalRows.length,canonicalDone,canonicalPaging,canonicalNextOffset,canonicalSourceGenerationId,scope,bandId]);
  useEffect(()=>{
    if(!canonicalMode||!points.length)return;
    if(range==="MAX"){canonicalPreserveRange.current=null;setViewportWindow({start:0,end:points.length-1});if(!canonicalDone&&!canonicalPaging)void loadCanonicalOlder(false);return;}
    const fallback=presetCanonicalViewport(points.length,range,resolution);
    const pending=canonicalPreserveRange.current;
    if(pending){canonicalPreserveRange.current=null;setViewportWindow(viewportForCanonicalDateRange(points,pending.startDate,pending.endDate,fallback));previousCanonicalResolution.current=resolution;return;}
    const previous=previousCanonicalResolution.current;
    if(previous&&previous!==resolution&&canonicalVisibleRange.current){const keep=canonicalVisibleRange.current;setViewportWindow(viewportForCanonicalDateRange(points,keep.startDate,keep.endDate,fallback));}
    previousCanonicalResolution.current=resolution;
  },[canonicalMode,points,range,resolution,canonicalDone,canonicalPaging]);
  useEffect(()=>{
    if(!canonicalMode){canonicalVisibleRange.current=null;previousCanonicalResolution.current=null;return;}
    if(visiblePoints.length)canonicalVisibleRange.current={startDate:visiblePoints[0].date,endDate:visiblePoints.at(-1)!.date};
  },[canonicalMode,visiblePoints]);
  const axisBottom = (show: boolean) => <XAxis dataKey="date" hide={!show} minTickGap={28} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(value) => fmtDate(value, resolution)} />;
  const sharedCrosshair = () => hoverPoint ? <ReferenceLine x={hoverPoint.date} stroke="#475569" strokeDasharray="3 3" strokeWidth={1} /> : null;
  const resetViewport = () => setViewportWindow(null);
  const zoomViewport = (factor: number, anchorRatio = 0.5) => {
    if (!rangePoints.length) return;
    const next=zoomViewportWindow(normalizedViewport,rangePoints.length,factor,anchorRatio);
    setViewportWindow(next);
    maybeLoadCanonicalOlder(next.start,next.end-next.start+1);
  };
  const panViewport = (shiftBars: number) => {
    if (!rangePoints.length) return;
    const next=panViewportWindow(normalizedViewport,rangePoints.length,shiftBars);
    setViewportWindow(next);
    if(shiftBars<0)maybeLoadCanonicalOlder(next.start,next.end-next.start+1);
  };
  const chartPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (rangePoints.length <= viewportSpan || (event.pointerType === "mouse" && event.button !== 0)) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const active = event.pointerType === "mouse" || event.pointerType === "pen";
    dragRef.current = { pointerId: event.pointerId, pointerType: event.pointerType, startX: event.clientX, startY: event.clientY, width: Math.max(1, rect.width - Y_AXIS_WIDTH - CHART_MARGIN.left - CHART_MARGIN.right), window: normalizedViewport, active };
    if (active) {
      event.currentTarget.setPointerCapture(event.pointerId);
      setIsPanning(true);
      event.preventDefault();
    }
  };
  const chartPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (!drag.active) {
      const deltaX = event.clientX - drag.startX;
      const deltaY = event.clientY - drag.startY;
      if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) >= 6) {
        dragRef.current = null;
        return;
      }
      if (Math.abs(deltaX) < 6) return;
      drag.active = true;
      event.currentTarget.setPointerCapture(event.pointerId);
      setIsPanning(true);
    }
    event.preventDefault();
    const size = drag.window.end - drag.window.start + 1;
    const shiftBars = ((drag.startX - event.clientX) / drag.width) * size;
    const next=panViewportWindow(drag.window,rangePoints.length,shiftBars);
    setViewportWindow(next);
    if(shiftBars<0)maybeLoadCanonicalOlder(next.start,size);
  };
  const endChartPan = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    setIsPanning(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  useEffect(() => {
    const node = interactionSurfaceRef.current;
    if (!node || tickMode) return;
    const handleWheel = (event: WheelEvent) => {
      if (rangePoints.length <= MIN_VIEWPORT_BARS || event.deltaY === 0) return;
      const current = normalizedViewport;
      const currentSize = current.end - current.start + 1;
      const zoomingIn = event.deltaY < 0;
      if ((!zoomingIn && currentSize >= rangePoints.length) || (zoomingIn && currentSize <= Math.min(rangePoints.length, MIN_VIEWPORT_BARS))) return;
      event.preventDefault();
      const rect = node.getBoundingClientRect();
      const drawableLeft = CHART_MARGIN.left;
      const drawableRight = Y_AXIS_WIDTH + CHART_MARGIN.right;
      const drawableWidth = Math.max(1, rect.width - drawableLeft - drawableRight);
      const anchorRatio = clampNumber((event.clientX - rect.left - drawableLeft) / drawableWidth, 0, 1);
      const boundedDelta = clampNumber(event.deltaY, -240, 240);
      const factor = Math.exp(boundedDelta * 0.0016);
      const next=zoomViewportWindow(normalizedViewport,rangePoints.length,factor,anchorRatio);
      setViewportWindow(next);
      maybeLoadCanonicalOlder(next.start,next.end-next.start+1);
    };
    node.addEventListener("wheel", handleWheel, { passive: false });
    return () => node.removeEventListener("wheel", handleWheel);
  }, [tickMode, rangePoints.length, normalizedViewport.start, normalizedViewport.end,canonicalMode,canonicalDone,canonicalPaging,canonicalNextOffset,canonicalSourceGenerationId,scope,bandId]);

  useEffect(() => {
    setRuntimeDiagnosticContext("housing-hts", {
      scope,
      bandId,
      officialScope,
      tickMode,
      resolutionSelection,
      recommendedResolution,
      resolution,
      range,
      priceBasisSelection,
      priceBasis,
      sourceBarCount: sourceBars.length,
      rawBarCount: rawBars.length,
      rangePointCount: rangePoints.length,
      visiblePointCount: visiblePoints.length,
      viewportStart: normalizedViewport.start,
      viewportEnd: normalizedViewport.end,
      viewportZoomed,
      viewportAtLatest,
      viewportStartDate,
      viewportEndDate,
      viewportHasPricePoint,
      tickCount: ticks.length,
      tickRangeCount: rangeTicks.length,
      tickCapApplied,
      showQualityOverlay,
      showRawOverlay,
      showFixedOverlay,
      fixedGapCount: fixedGapPoints.length,
      fixedResetCount,
      fixedLatestGapReason,
      fixedReadyShare,
      fixedMatureReadyShare: resolutionQuality.fixedMatureReadyShare,
      qualityAdjustedReadyShare,
      rawReadyShare,
      basisLagDays,
      autoFallbackUsed,
      basisNotice,
      currentSegmentLength,
      resolutionQuality,
      showPriceMa,
      subIndicators
    });
  }, [scope, bandId, officialScope, tickMode, resolutionSelection, recommendedResolution, resolution, range, priceBasisSelection, priceBasis, sourceBars.length, rawBars.length, rangePoints.length, visiblePoints.length, normalizedViewport.start, normalizedViewport.end, viewportZoomed, viewportAtLatest, viewportStartDate, viewportEndDate, viewportHasPricePoint, ticks.length, rangeTicks.length, tickCapApplied, showQualityOverlay, showRawOverlay, showFixedOverlay, fixedGapPoints.length, fixedResetCount, fixedLatestGapReason, fixedReadyShare, qualityAdjustedReadyShare, rawReadyShare, basisLagDays, currentSegmentLength, resolutionQuality, autoFallbackUsed, basisNotice, showPriceMa, subIndicators]);

  return (
    <section ref={exportRef} className="housing-tech-chart housing-hts">

      {data?.sourceTicksStatus === "not_in_analysis_cache" && <p className="housing-tech-empty" role="status">이 차트는 게시된 분석 세대의 집계 자료입니다. 개별 원거래 목록은 분석 캐시에 포함되지 않아 실거래 Tape를 제공하지 않습니다. 거래가 0건이라는 뜻은 아닙니다.</p>}
      <header className="housing-hts__topline" data-export-section="header">
        <div className="housing-hts__title">
          <div className="housing-hts__heading-row">
            <p className="kicker">H2.7 · HOUSING HTS</p>
            <span className="housing-hts__help-wrap" data-export-hide="true">
              <button type="button" className="housing-hts__help-button" aria-label="HTS 지수화와 OHLC 설명" aria-describedby="housing-hts-index-help">?</button>
              <div id="housing-hts-index-help" className="housing-hts__help-popover" role="tooltip">
                <strong>지수·OHLC를 이렇게 읽습니다</strong>
                <div>
                  <b>Quality-adjusted · 기본 시장선</b>
                  <p>지역코드×법정동×단지×전용면적 5㎡ cell마다 최근 수집구간 전체의 자기 기준가격(anchor)을 잡고, 각 거래를 그 cell 기준보다 몇 % 위·아래인지로 바꿔 합칩니다. 같은 cell의 하루 여러 거래는 중앙값 하나로 먼저 줄입니다. <em>인접 봉에서 같은 cell이 다시 거래될 필요가 없어서 Strict Fixed보다 훨씬 연속적</em>입니다. 다만 정식 hedonic 회귀나 동일주택 repeat-sales는 아니며, anchor가 retrospective라 backfill·취소·정정으로 과거 값이 달라질 수 있습니다.</p>
                </div>
                <div>
                  <b>Raw observed · 실제 거래 중앙</b>
                  <p>각 봉에서 실제로 거래된 아파트 가격의 중앙값을 그대로 기준 100으로 재표시합니다. 어느 지역·단지·평형이 많이 거래됐는지가 값에 직접 섞이므로 <em>구성효과를 확인하는 진단선</em>으로 사용합니다. 당기 거래 중앙의 원화값도 상단과 상세정보에 함께 표시합니다.</p>
                </div>
                <div>
                  <b>Strict Fixed · 동일 cell 인접비교</b>
                  <p>인접 봉에 모두 존재하는 같은 단지×5㎡ cell의 가격변화만 계산합니다. 가격대 경계를 넘어도 이전 봉의 origin cohort를 추적하지만, 공통셀이 부족하면 값을 만들지 않습니다. 새 연속구간은 100에서 다시 시작하고 MA·MACD·RSI도 reset합니다. <em>가장 엄격하지만 짧은 봉에서는 희소하므로 기본 시장선이 아니라 검증용</em>입니다.</p>
                </div>
                <div>
                  <b>OHLC · AUTO · 비교선</b>
                  <p>Quality-adjusted의 1D는 O=직전 유효 C, C=현재 지수, H/L=max/min(O,C)이고 상위 봉은 유효 1D를 재집계합니다. Raw observed는 각 봉 거래가격 중앙값을 기준 100으로 환산하고 O=직전 Raw C, C=현재 중앙값, H/L=max/min(O,C)인 합성 경로로 표시합니다. RTMS에는 장중 체결순서가 없으므로 wick을 실제 장중 고저가로 해석하지 않습니다. AUTO는 7일→14일→월봉 중 가장 짧은 신뢰 가능한 <em>Quality-adjusted</em> 봉을 고르며 Strict Fixed로 자동 전환하지 않습니다. 비교선은 같은 시점 수준으로 재기준해 방향과 괴리를 비교합니다.</p>
                </div>
                <div>
                  <b>차트 조작 · 기간과 화면창</b>
                  <p><em>기간 1M/3M/6M/1Y/MAX는 전체 보존 이력을 자르는 옵션이 아니라 viewport 바로가기</em>입니다. 차트 위 마우스 휠로 커서 위치를 중심으로 확대·축소하고, 왼쪽 끝에 접근하면 repository의 더 오래된 canonical 1D를 bounded lazy-load합니다. 확대된 상태에서 좌우 드래그로 시간을 이동하고, 더블클릭 또는 초기화 버튼은 선택한 기간 화면창으로 돌아갑니다. 모바일은 좌우 스와이프로 이동하고 +/− 버튼으로 확대·축소합니다. 가격·거래량·MACD·RSI는 항상 같은 화면창을 공유하며 Y축은 보이는 데이터에 맞춰 자동 재조정됩니다. 실거래 Tape는 원거래 최대 300건 표시 원칙을 유지하므로 이 viewport zoom/pan을 적용하지 않습니다.</p>
                  <p><em>하단 시계열 확보 현황</em>은 RTMS 수집 완료월은 연속구간별로, 파생/기술지표는 현재 지역·가격대·봉의 HTS payload에서 실제 계산 가능한 연속구간별로 보여줍니다. 예를 들어 2006~2012와 2024~2026만 확보됐다면 두 구간을 따로 표시합니다.</p>
                </div>
              </div>
            </span>
          </div>
          <h4>{SCOPE_LABELS[scope]} · {bandLabel}</h4>
          <div className="housing-hts__quote">
            {tickMode ? <>
              <strong>{latestTick ? eok(latestTick.priceKrw) : "N/A"}</strong>
              <span>{ticks.length.toLocaleString()}건</span>
              <small>최근 RTMS 계약 · {latestTick?.date ?? "-"} · 장중 체결시각 없음</small>
            </> : <>
              <strong className={activePoint ? `is-${activePoint.candleDirection}` : ""}>{activePoint?.candleClose == null ? "N/A" : activePoint.candleClose.toFixed(2)}</strong>
              <span className={activePoint ? `is-${activePoint.candleDirection}` : ""}>{signed(activePoint?.closeChangePct)}%</span>
              <small>{basisLagDays > 0 ? "마지막 계산 가능 가격봉" : "지수"}{officialScope ? "" : ` · 당기 거래 중앙 ${eok(activePoint?.medianPriceProxyKrw)}`} · {activePoint?.date ?? "-"} · {resolutionLabel(resolution)}</small>
            </>}
          </div>
        </div>
        <div className="housing-tech-chart__badges">
          {tickMode ? <span className="housing-hts__badge--basis">실거래 Tape · 계약일 기준</span> : <>
            <span className="housing-hts__badge--basis">현재 · {priceBasisLabel(priceBasis)}</span>
            <span className={`housing-hts__quality-badge is-${basisQualityClass}`}>{priceBasis === "fixed_cell" ? `Strict 가용 ${Math.round(resolutionQuality.fixedReadyShare * 100)}% · 성숙 ${Math.round(resolutionQuality.fixedMatureReadyShare * 100)}%` : priceBasis === "raw_median" ? `Raw 가용 ${Math.round(resolutionQuality.rawReadyShare * 100)}%` : officialScope ? "공식지수" : `QA 가용 ${Math.round(resolutionQuality.observedReadyShare * 100)}%`}</span>
            {(resolutionSelection === "auto" || officialScope) && <span className={autoFallbackUsed ? "is-warn" : undefined}>{officialScope ? "공식 월봉" : `자동 봉 → ${resolutionLabel(resolution)}${autoFallbackUsed ? " · 품질기준 미달 최선대체" : ""}`}</span>}
            {!officialScope && priceBasisSelection === "auto" && <span>자동 가격기준 → Quality-adjusted</span>}
            {priceBasis === "fixed_cell" && fixedSegmentStarts.length > 0 && <span>연속구간 {fixedSegmentStarts.length}개{fixedResetCount ? ` · 재기준 ${fixedResetCount}회` : ""}</span>}
            {latestDisplayPoint?.provisional && <span className="is-warn">진행중 봉</span>}
            {!officialScope && activePoint && activePoint.initialReportingMature === false && <span className="is-warn">초기 신고창 &lt;30일</span>}
            {!officialScope && finite(activePoint?.compositionGapPct) && Math.abs(activePoint.compositionGapPct) >= 0.5 && <span className="is-warn">QA↔Strict 괴리 {signed(activePoint.compositionGapPct)}%p</span>}
            {!officialScope && finite(activePoint?.bandMigrationCellPct) && activePoint.bandMigrationCellPct >= 10 && <span className="is-warn">가격대 이동 {activePoint.bandMigrationCellPct.toFixed(0)}%</span>}
            {basisLagDays > 0 && <span className="is-warn">가격지수 최신값 {basisLagDays}일 지연</span>}
            {pendingBarsAfterLatest > 0 && <span className="is-warn">최근 계산불가 {pendingBarsAfterLatest}봉</span>}
            {!viewportAtLatest && viewportEndDate && <span>과거 화면창 · {viewportEndDate}</span>}
            {lowSampleShare >= 0.3 && <span className="is-warn">저표본 {Math.round(lowSampleShare * 100)}%</span>}
            {!officialScope && priceBasis === "median_proxy" && resolution !== "1d" && resolutionQuality.medianValidDayShare < 0.35 && <span className="is-warn">QA 일별 경로 희박 {Math.round(resolutionQuality.medianValidDayShare * 100)}%</span>}
            {!fixedLatestGapReason && showPriceMa && currentSegmentLength < longestMa && <span className="is-warn">MA{longestMa} 대기 {currentSegmentLength}/{longestMa}</span>}
            {!fixedLatestGapReason && subIndicators.macd && currentSegmentLength < macdRequiredBars && <span className="is-warn">MACD 대기 {currentSegmentLength}/{macdRequiredBars}</span>}
          </>}
        </div>
      </header>

      <div className="housing-hts__toolbar" data-export-hide="true">
        <label className="housing-hts__control">
          <span>지역</span>
          <select aria-label="시장" value={scope} onChange={(event) => { setBasisNotice(null); setScope(event.target.value as HousingChartScopeId); }}>
            {SCOPE_ORDER.map((id) => <option key={id} value={id}>{SCOPE_LABELS[id]}</option>)}
          </select>
        </label>
        <label className="housing-hts__control">
          <span>가격대</span>
          <select aria-label="가격대" value={bandId} disabled={officialScope} onChange={(event) => { setBasisNotice(null); setBandId(event.target.value); }}>
            {bandOptions.map((band) => <option key={band.id} value={band.id}>{band.label}</option>)}
          </select>
        </label>
        <label className="housing-hts__control housing-hts__control--basis">
          <span>가격기준</span>
          <select aria-label="가격기준" value={officialScope ? "official_index" : priceBasisSelection} disabled={officialScope || tickMode} onChange={(event) => {
            const next = event.target.value as HousingChartPriceBasis;
            setBasisNotice(null);
            setPriceBasisSelection(next);
            if (next === "fixed_cell" && (resolutionSelection === "1d" || resolutionSelection === "3d" || resolutionSelection === "tick")) setResolutionSelection("auto");
          }}>
            {officialScope ? <option value="official_index">공식 아파트 매매가격지수</option> : <>
              <option value="auto">자동 · Quality-adjusted 기본</option>
              <option value="median_proxy">Quality-adjusted · cell 정규화</option>
              <option value="raw_median">Raw observed · 실제 거래 중앙</option>
              <option value="fixed_cell" disabled={!hasAnyFixed}>Strict Fixed · 동일 셀 인접비교{!hasAnyFixed ? " (현재 자료없음)" : ""}</option>
            </>}
          </select>
        </label>
        <div className="housing-hts__control housing-hts__control--buttons">
          <span>봉</span>
          <div className="housing-hts__segmented housing-hts__segmented--resolution">
            {RESOLUTION_OPTIONS.map((item) => {
              const fixedUnsupported = priceBasisSelection === "fixed_cell" && (item.id === "1d" || item.id === "3d" || item.id === "tick");
              const disabled = (officialScope && item.id !== "auto" && item.id !== "1m") || fixedUnsupported;
              const active = officialScope ? item.id === "1m" : resolutionSelection === item.id;
              return <button key={item.id} type="button" disabled={disabled} className={active ? "is-active" : ""} title={fixedUnsupported ? "Strict Fixed는 7일·14일·월봉에서만 계산합니다." : undefined} onClick={() => !disabled && setResolutionSelection(item.id)}>{item.label}</button>;
            })}
          </div>
        </div>
        <div className="housing-hts__control housing-hts__control--buttons">
          <span>기간</span>
          <div className="housing-hts__segmented housing-hts__segmented--range">
            {RANGE_OPTIONS.map((item) => <button key={item} type="button" className={range === item ? "is-active" : ""} onClick={() => { setRange(item); setViewportWindow(null); }}>{item}</button>)}
          </div>
        </div>
        <div className="housing-hts__toolbar-actions">
          <div className="housing-hts__tool-popover-wrap">
            <button type="button" disabled={tickMode} className={`housing-hts__tool-button ${showIndicatorMenu ? "is-active" : ""}`} onClick={() => { setShowIndicatorMenu((value) => !value); setShowSettings(false); }}>지표 ▾</button>
            {showIndicatorMenu && !tickMode && (
              <div className="housing-hts__popover housing-hts__indicator-menu">
                <strong>오버레이</strong>
                <label><input type="checkbox" checked={showPriceMa} onChange={(event) => setShowPriceMa(event.target.checked)} /> 이동평균선 MA</label>
                {priceBasis !== "median_proxy" && <label className={!qualityComparisonAvailable ? "is-disabled" : ""}><input type="checkbox" checked={showQualityComparison && qualityComparisonAvailable} disabled={!qualityComparisonAvailable} onChange={(event) => setShowQualityComparison(event.target.checked)} /> Quality-adjusted 비교선</label>}
                {priceBasis !== "raw_median" && <label className={!rawComparisonAvailable ? "is-disabled" : ""}><input type="checkbox" checked={showRawComparison && rawComparisonAvailable} disabled={!rawComparisonAvailable} onChange={(event) => setShowRawComparison(event.target.checked)} /> Raw observed 비교선</label>}
                {priceBasis !== "fixed_cell" && <label className={!fixedComparisonAvailable ? "is-disabled" : ""}><input type="checkbox" checked={showFixedComparison && fixedComparisonAvailable} disabled={!fixedComparisonAvailable} onChange={(event) => setShowFixedComparison(event.target.checked)} /> Strict Fixed 비교선</label>}
                <strong>보조지표</strong>
                <label className={officialScope ? "is-disabled" : ""}><input type="checkbox" checked={subIndicators.volume && !officialScope} disabled={officialScope} onChange={(event) => setSubIndicators((v) => ({ ...v, volume: event.target.checked }))} /> 거래량 + 거래량 MA</label>
                <label><input type="checkbox" checked={subIndicators.macd} onChange={(event) => setSubIndicators((v) => ({ ...v, macd: event.target.checked }))} /> MACD / Signal / Osc</label>
                <label><input type="checkbox" checked={subIndicators.rsi} onChange={(event) => setSubIndicators((v) => ({ ...v, rsi: event.target.checked }))} /> RSI 14</label>
              </div>
            )}
          </div>
          <div className="housing-hts__tool-popover-wrap">
            <button type="button" disabled={tickMode} className={`housing-hts__tool-button housing-hts__tool-button--icon ${showSettings ? "is-active" : ""}`} aria-label="보조지표 설정" title="보조지표 설정" onClick={() => { setShowSettings((value) => !value); setShowIndicatorMenu(false); }}>⚙</button>
            {showSettings && !tickMode && (
              <div className="housing-hts__popover housing-hts__settings">
                <strong>보조지표 설정</strong>
                <span>MACD</span>
                <div className="housing-hts__segmented">
                  <button type="button" className={macdPreset === "fast" ? "is-active" : ""} onClick={() => setMacdPreset("fast")}>빠른 6/12/4</button>
                  <button type="button" className={macdPreset === "standard" ? "is-active" : ""} onClick={() => setMacdPreset("standard")}>표준 12/26/9</button>
                </div>
                <small>MA·MACD·RSI는 달력일이 아니라 유효 가격봉 기준입니다. 예: MA7 = 최근 7개 유효봉 평균이며 저표본/결측봉은 제외됩니다.</small>
              </div>
            )}
          </div>
          <button
            type="button"
            className="housing-hts__tool-button housing-hts__export-button"
            disabled={exporting || loading || !data}
            onClick={async () => {
              if (!exportRef.current || exporting) return;
              setExportError(null);
              setExporting(true);
              setRuntimeDiagnosticContext("housing-hts-png-export", { mode: "canvas-dom-range-inline-svg", status: "running", scope, bandId, resolution, range });
              try {
                const stamp = new Date().toISOString().slice(0, 10).replaceAll("-", "");
                const name = safeExportName(`FREM_${SCOPE_LABELS[scope]}_${bandLabel}_${resolutionLabel(resolution)}_${range}_${stamp}`);
                const exportResult = await exportElementPng(exportRef.current, `${name}.png`);
                setRuntimeDiagnosticContext("housing-hts-png-export", { mode: "canvas-dom-range-inline-svg", status: "success", scope, bandId, resolution, range, filename: `${name}.png`, ...exportResult });
              } catch (reason) {
                recordRuntimeDiagnosticError("housing-hts-png-export", reason, { kind: "operation-error" });
                setRuntimeDiagnosticContext("housing-hts-png-export", { mode: "canvas-dom-range-inline-svg", status: "error", scope, bandId, resolution, range, message: reason instanceof Error ? reason.message : String(reason) });
                setExportError(reason instanceof Error ? reason.message : String(reason));
              } finally {
                setExporting(false);
              }
            }}
            title="현재 HTS 차트를 PNG 이미지로 저장합니다."
          >{exporting ? "저장 중…" : "PNG 저장"}</button>
        </div>
        {exportError && <small className="housing-hts__export-error">PNG 저장 실패: {exportError}</small>}
      </div>

      <div className={`housing-hts__basis-summary housing-hts__basis-summary--${tickMode ? "tape" : priceBasis}`} data-export-section="basis-summary">
        {tickMode ? <>
          <strong>실거래 원자료</strong>
          <span>계약일 기준 최근 거래를 실제 원화가격으로 봅니다. 지수·Quality-adjusted·Strict Fixed·MA·MACD는 적용하지 않으며 같은 날짜의 거래는 같은 X위치에 표시합니다. 원거래 최대 300건 표시를 보존하기 위해 지수차트의 viewport zoom/pan은 Tape에는 적용하지 않습니다.</span>
          <em>{range} 범위 · {tickCapApplied ? `표시 ${ticks.length} / 범위 내 ${rangeTicks.length}건` : `${ticks.length}건`} · 최대 300건</em>
        </> : <>
          <strong>{priceBasisLabel(priceBasis)}</strong>
          <span>{priceBasisDescription(priceBasis)}</span>
          {!officialScope && priceBasisSelection === "auto" && <em className={autoFallbackUsed ? "is-warn" : undefined}>{autoFallbackUsed ? "자동 선택 · 품질기준 미달 최선대체" : "자동 선택 결과"}</em>}
          {!officialScope && <em className={`is-${basisQualityClass}`}>{priceBasis === "fixed_cell" ? `최근 Strict 가용 ${Math.round(resolutionQuality.fixedReadyShare * 100)}% · 성숙 ${Math.round(resolutionQuality.fixedMatureReadyShare * 100)}%` : priceBasis === "raw_median" ? `최근 Raw 가용 ${Math.round(resolutionQuality.rawReadyShare * 100)}%` : `최근 QA 가용 ${Math.round(resolutionQuality.observedReadyShare * 100)}%`}</em>}
          {priceBasis === "fixed_cell" && fixedSegmentStarts.length > 0 && <em>Strict 연속구간 {fixedSegmentStarts.length}개 · 재기준 {fixedResetCount}회 · gap {fixedGapPoints.length}봉</em>}
          {basisNotice && <em className="is-warn">{basisNotice}</em>}
          {!officialScope && resolutionSelection !== "auto" && recommendedResolution && resolution !== recommendedResolution && ((priceBasis === "fixed_cell" && !resolutionQuality.fixedUsable) || (priceBasis === "raw_median" && !resolutionQuality.rawUsable) || (priceBasis === "median_proxy" && !resolutionQuality.observedUsable)) && <button type="button" className="housing-hts__recommend-button" data-export-hide="true" onClick={() => setResolutionSelection(recommendedResolution)}>권장 {resolutionLabel(recommendedResolution)}로 보기</button>}
          {!officialScope && resolutionSelection !== "auto" && !recommendedResolution && ((priceBasis === "fixed_cell" && !resolutionQuality.fixedUsable) || (priceBasis === "raw_median" && !resolutionQuality.rawUsable) || (priceBasis === "median_proxy" && !resolutionQuality.observedUsable)) && <em className="is-warn">현재 품질기준을 통과한 권장 봉 없음</em>}
          {fixedLatestGapReason && <em className="is-warn">최신 Strict gap · {fixedLatestGapReason}</em>}
        </>}
      </div>

      {!tickMode && <div className="housing-hts__selected-indicators" data-export-section="selected-indicators">
        <span className="housing-hts__selected-indicators-label">표시 중</span>
        {showPriceMa && <button type="button" onClick={() => setShowPriceMa(false)}>MA {config.priceMaPeriods.join("/")} ×</button>}
        {showQualityOverlay && <button type="button" onClick={() => setShowQualityComparison(false)}>Quality-adjusted 비교 ×</button>}
        {showRawOverlay && <button type="button" onClick={() => setShowRawComparison(false)}>Raw observed 비교 ×</button>}
        {showFixedOverlay && <button type="button" onClick={() => setShowFixedComparison(false)}>Strict Fixed 비교 ×</button>}
        {subIndicators.volume && !officialScope && <button type="button" onClick={() => setSubIndicators((v) => ({ ...v, volume: false }))}>거래량 ×</button>}
        {subIndicators.macd && <button type="button" onClick={() => setSubIndicators((v) => ({ ...v, macd: false }))}>MACD {config.macdFast}/{config.macdSlow}/{config.macdSignal} ×</button>}
        {subIndicators.rsi && <button type="button" onClick={() => setSubIndicators((v) => ({ ...v, rsi: false }))}>RSI {config.rsiPeriod} ×</button>}
      </div>}

      {loading && <div className="housing-tech-empty">HTS 차트 데이터를 불러오는 중입니다.</div>}
      {error && <div className="housing-tech-empty"><strong>차트 데이터 N/A</strong><span>{error}</span><small>{canonicalMode ? "전국·비수도권은 SDP RTMS coverage 확장 전까지 RTMS 자체지수를 제공하지 않습니다. 지원 scope는 repository canonical 1D만 읽습니다." : officialScope ? "공식 ECOS 메타데이터가 정확히 확인되면 자동 연결됩니다." : "SDP 중앙 이관 차트 파일을 읽지 못했습니다. 월별 RTMS 집계는 별도로 계속 표시됩니다."}</small></div>}

      {!loading && !error && data && !officialScope && resolutionSelection === "tick" && (
        <div className="housing-hts__frame housing-hts__tick-frame" data-export-section="chart-frame">
          <div className="housing-hts__pane housing-hts__pane--tick">
            <div className="housing-hts__pane-legend"><strong>실거래 Tape</strong><span>{tickCapApplied ? `표시 ${ticks.length.toLocaleString()} / 범위 내 ${rangeTicks.length.toLocaleString()}건` : `${ticks.length.toLocaleString()}건`}</span></div>
            <StableChartViewport height={300}>{(chartWidth) => (
              <ScatterChart width={chartWidth} height={300} margin={{ top: 26, right: 62, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="2 2" opacity={0.15} />
                <XAxis type="number" dataKey="time" domain={["dataMin", "dataMax"]} tick={{ fontSize: 9 }} axisLine={false} tickLine={false} minTickGap={36} tickFormatter={(value: number) => new Date(value).toISOString().slice(2, 10).replaceAll("-", ".")} />
                <YAxis orientation="right" type="number" dataKey="priceEok" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={Y_AXIS_WIDTH} tickFormatter={(value: number) => `${value.toFixed(0)}억`} domain={["auto", "auto"]} />
                <Tooltip formatter={(value: unknown) => [`${Number(value).toFixed(1)}억`, "거래가격"]} labelFormatter={(value) => Number.isFinite(Number(value)) ? new Date(Number(value)).toISOString().slice(0, 10) : "계약일"} />
                <Scatter name="거래가격" data={tickPlot} fill={UP_COLOR} isAnimationActive={false} />
              </ScatterChart>
            )}</StableChartViewport>
          </div>
          <div className="housing-tech-tape housing-hts__tape">
            <div className="housing-hts__pane-legend"><strong>실거래 목록</strong><span>RTMS는 체결시각 미제공 · 계약일 기준</span></div>
            <div className="housing-tech-tape__scroll">
              <table><thead><tr><th>일자</th><th>단지</th><th>면적</th><th>층</th><th>가격</th></tr></thead><tbody>{ticks.slice(0, 80).map((tick, i) => <tr key={`${tick.date}-${tick.complexName}-${tick.priceKrw}-${i}`}><td>{tick.date.slice(5)}</td><td><strong>{tick.sigungu} {tick.legalDong}</strong><small>{tick.complexName}</small></td><td>{tick.exclusiveAreaM2.toFixed(1)}㎡</td><td>{tick.floor ?? "-"}</td><td>{eok(tick.priceKrw)}</td></tr>)}</tbody></table>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && data && (officialScope || resolutionSelection !== "tick") && (
        <>
          <div className="housing-tech-warning">{officialScope ? <>한국부동산원 <strong>공식 월간 아파트 매매가격지수</strong>입니다. 월 1개 관측값만 있으므로 O는 직전 월 C, C는 현재 월 지수, H/L은 O·C 범위이며 거래량·실거래 목록은 제공하지 않습니다.</> : priceBasis === "fixed_cell" ? <>주 가격선은 <strong>Strict Fixed</strong>입니다. 인접 봉의 동일 단지×5㎡ cell만 추적하며 가격대 경계 이동은 origin cohort로 보존합니다. 공통셀 guard가 부족한 기간은 <strong>실제 계산불가 gap</strong>으로 남기고 다음 연속구간은 100으로 재기준합니다. 가격 MA·MACD·RSI도 새 segment에서 reset하지만 거래량은 계속 표시합니다.{fixedLatestGapReason ? <> 최신 구간은 <strong>{fixedLatestGapReason}</strong> 때문에 Strict 가격선이 계산되지 않습니다.</> : null}</> : priceBasis === "raw_median" ? <>주 가격선은 <strong>Raw observed</strong>입니다. 각 봉 실제 거래가격 중앙값을 기준 100으로 재표시하므로 지역·단지·평형 거래구성이 바뀌면 그 변화가 그대로 가격선에 섞입니다. 시장 방향의 주 판단보다는 <strong>Quality-adjusted와의 괴리를 확인하는 진단용</strong>으로 봅니다.</> : <>주 가격선은 <strong>Quality-adjusted(cell 정규화)</strong>입니다. 단지×5㎡ cell별 retrospective anchor를 제거한 상대가격을 합성하여 Raw observed보다 거래구성 영향을 줄이되, 인접 봉 재거래를 요구하지 않아 Strict Fixed보다 연속적입니다. 정식 hedonic/repeat-sales 또는 as-released PIT 지수는 아닙니다.{resolution !== "1d" && resolutionQuality.medianValidDayShare < 0.35 ? <> 현재 봉은 유효 일별 가격경로가 달력의 약 <strong>{Math.round(resolutionQuality.medianValidDayShare * 100)}%</strong>뿐이라 OHLC 경로 해석은 낮은 신뢰도로 봐야 합니다.</> : null}</>}</div>
          {!officialScope && activePoint && <div className="housing-hts__statusbar" data-export-section="statusbar">
            <span><strong>거래</strong> {activePoint.transactionCount?.toLocaleString() ?? 0}건</span>
            {canonicalMode ? <span><strong>Canonical 1D</strong> 상위봉은 1D OHLC 집계 · gap 무보간</span> : <span><strong>Quality-adjusted</strong> {activePoint.marketIndexStatus === "ready" ? `유효일 ${activePoint.marketIndexValidDayCount ?? 0}일` : "가격지수 계산불가"}</span>}
            {activePoint.fixedCellStatus === "ready" ? <>
              <span><strong>Strict 공통셀</strong> {activePoint.matchedCellCount ?? 0}/{activePoint.originCellCount ?? 0}</span>
              <span>cohort 포착 {finite(activePoint.originPreviousTransactionCoveragePct) ? `${activePoint.originPreviousTransactionCoveragePct.toFixed(0)}%` : "N/A"}</span>
              <span>셀 생존 {finite(activePoint.originCellSurvivalPct) ? `${activePoint.originCellSurvivalPct.toFixed(0)}%` : "N/A"}</span>
              <span>상승 breadth {finite(activePoint.fixedCellPositiveBreadthPct) ? `${activePoint.fixedCellPositiveBreadthPct.toFixed(0)}%` : "N/A"}</span>
              <span>migration {finite(activePoint.bandMigrationCellPct) ? `${activePoint.bandMigrationCellPct.toFixed(1)}%` : "N/A"}</span>
              <span>QA↔Strict gap {signed(activePoint.compositionGapPct)}%p</span>
              {scope === "capital" && <span>지역mix gap {signed(activePoint.regionMixGapPct)}%p</span>}
              <span>cutoff±{finite(activePoint.cutoffBufferPct) ? activePoint.cutoffBufferPct.toFixed(0) : "1"}% 인접 {finite(activePoint.originCellsNearCutoffPct) ? `${activePoint.originCellsNearCutoffPct.toFixed(1)}%` : "N/A"}</span>
            </> : <span className="is-warn"><strong>Strict Fixed 계산불가</strong> {fixedGapReason(activePoint, data.fixedCellGuard)}</span>}
            <span>{activePoint.initialReportingMature ? "초기 신고창 경과" : `신고성숙 ${activePoint.reportingAgeDays ?? 0}일`}</span>
          </div>}
          {rangeHasPricePoint ? <div className="housing-hts__frame" data-export-section="chart-frame">
            <div className="housing-hts__viewport-toolbar" data-export-section="viewport-summary">
              <div className="housing-hts__viewport-summary">
                <strong>화면 · 기간 {range}</strong>
                <span>{viewportStartDate ?? "-"} ~ {viewportEndDate ?? "-"}</span>
                <em>{viewportSpan.toLocaleString()} / {rangePoints.length.toLocaleString()}봉</em>
                {!viewportHasPricePoint && <em className="is-warn">현재 화면창 가격 N/A</em>}
                <small data-export-hide="true">휠 확대·축소 · 좌우 드래그 이동 · 더블클릭 초기화</small>
              </div>
              <div className="housing-hts__viewport-actions" data-export-hide="true" aria-label="차트 화면 이동 확대 축소">
                <button type="button" aria-label="이전 시간대로 이동" title="이전 시간대로 이동" disabled={!viewportZoomed || (normalizedViewport.start <= 0 && (!canonicalMode || canonicalDone))} onClick={() => panViewport(-Math.max(1, Math.round(viewportSpan * 0.25)))}>‹</button>
                <button type="button" aria-label="차트 축소" title="더 넓은 기간 보기" disabled={!viewportZoomed} onClick={() => zoomViewport(1.35)}>−</button>
                <button type="button" aria-label="차트 확대" title="더 좁은 기간 보기" disabled={viewportSpan <= Math.min(rangePoints.length, MIN_VIEWPORT_BARS)} onClick={() => zoomViewport(0.74)}>+</button>
                <button type="button" aria-label="다음 시간대로 이동" title="다음 시간대로 이동" disabled={!viewportZoomed || normalizedViewport.end >= rangePoints.length - 1} onClick={() => panViewport(Math.max(1, Math.round(viewportSpan * 0.25)))}>›</button>
                <button type="button" disabled={!viewportZoomed && normalizedViewport.start === 0} onClick={resetViewport}>초기화</button>
              </div>
            </div>
            <div
              ref={interactionSurfaceRef}
              className={`housing-hts__interaction-surface ${isPanning ? "is-panning" : ""}`}
              role="group"
              aria-label="차트 화면: 휠 확대 축소, 드래그 시간 이동"
              onPointerDown={chartPointerDown}
              onPointerMove={chartPointerMove}
              onPointerUp={endChartPan}
              onPointerCancel={endChartPan}
              onDoubleClick={resetViewport}
            >
            <div className="housing-hts__pane housing-hts__pane--price">
              <div className="housing-hts__pane-legend housing-hts__pane-legend--price">
                <strong>{activePoint?.date ?? "-"}</strong>
                {activeFixedGapReason ? <span className="is-warn">Strict Fixed N/A · {activeFixedGapReason}</span> : <>
                  <span>시 {activePoint?.candleOpen?.toFixed(2) ?? "-"}</span>
                  <span>고 {activePoint?.candleHigh?.toFixed(2) ?? "-"}</span>
                  <span>저 {activePoint?.candleLow?.toFixed(2) ?? "-"}</span>
                  <span>종 <b className={activePoint ? `is-${activePoint.candleDirection}` : ""}>{activePoint?.candleClose?.toFixed(2) ?? "-"}</b></span>
                </>}
                {!officialScope && <span>당기 거래 중앙 {eok(activePoint?.medianPriceProxyKrw)}</span>}
                {showPriceMa && config.priceMaPeriods.map((period, i) => <span key={period} style={{ color: LINE_COLORS[i] }}>MA{period} {finite(activePoint?.[PRICE_MA_KEYS[i]]) ? Number(activePoint?.[PRICE_MA_KEYS[i]]).toFixed(2) : currentSegmentLength < period ? `대기 ${currentSegmentLength}/${period}` : "-"}</span>)}
                {showQualityOverlay && <span style={{ color: "#111827" }}>Quality-adjusted(점선) {finite(activePoint?.qualityAdjustedComparisonIndex) ? activePoint.qualityAdjustedComparisonIndex.toFixed(2) : "-"}</span>}
                {showRawOverlay && <span style={{ color: "#64748b" }}>Raw observed(점선) {finite(activePoint?.rawObservedComparisonIndex) ? activePoint.rawObservedComparisonIndex.toFixed(2) : "-"}</span>}
                {showFixedOverlay && <span style={{ color: "#111827" }}>Strict Fixed(점선) {finite(activePoint?.fixedCellIndex) ? activePoint.fixedCellIndex.toFixed(2) : "-"}</span>}
              </div>
              <StableChartViewport height={390}>{(chartWidth) => (
                <ComposedChart width={chartWidth} height={390} syncId={SYNC_ID} data={visiblePoints} margin={{ ...CHART_MARGIN, top: 34 }} onMouseMove={chartMouseMove} onMouseLeave={() => setHoverPoint(null)}>
                  <CartesianGrid strokeDasharray="2 2" vertical={false} opacity={0.14} />
                  {axisBottom(false)}
                  <YAxis orientation="right" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={Y_AXIS_WIDTH} domain={["auto", "auto"]} tickFormatter={(value: number) => value.toFixed(1)} />
                  <Tooltip cursor={{ stroke: "#475569", strokeDasharray: "3 3", strokeWidth: 1 }} content={() => null} />
                  {sharedCrosshair()}
                  {priceBasis === "fixed_cell" && fixedSegmentStarts.slice(1).map((segment) => <ReferenceLine key={segment.id} x={segment.date} stroke="#94a3b8" strokeDasharray="2 4" strokeWidth={1} opacity={0.85} label={fixedSegmentStarts.length <= 7 ? { value: "100 재기준", position: "insideTopLeft", fill: "#64748b", fontSize: 9 } : undefined} />)}
                  {latestPricePoint && finite(latestPricePoint.candleClose) && <ReferenceLine y={latestPricePoint.candleClose as number} stroke={pointColor(latestPricePoint)} strokeDasharray="4 3" strokeWidth={1} opacity={0.7} label={{ value: latestPricePoint.candleClose.toFixed(2), position: "insideRight", fill: pointColor(latestPricePoint), fontSize: 10 }} />}
                  <Bar dataKey="candleRange" shape={<CandleShape />} maxBarSize={22} barSize={adaptiveBarSize(chartWidth, visiblePoints.length)} isAnimationActive={false} />
                  {showQualityOverlay && <Line type="linear" dataKey="qualityAdjustedComparisonIndex" name="Quality-adjusted 비교선" stroke="#111827" strokeWidth={1.5} strokeDasharray="5 5" opacity={0.76} dot={false} connectNulls={false} isAnimationActive={false} />}
                  {showRawOverlay && <Line type="linear" dataKey="rawObservedComparisonIndex" name="Raw observed 비교선" stroke="#64748b" strokeWidth={1.3} strokeDasharray="2 5" opacity={0.72} dot={false} connectNulls={false} isAnimationActive={false} />}
                  {showFixedOverlay && <Line type="linear" dataKey="fixedCellIndex" name="Strict Fixed 비교선" stroke="#111827" strokeWidth={1.6} strokeDasharray="7 5" opacity={0.86} dot={false} connectNulls={false} isAnimationActive={false} />}
                  {showPriceMa && config.priceMaPeriods.map((period, i) => <Line key={period} type="linear" dataKey={PRICE_MA_KEYS[i]} name={`MA${period}`} stroke={LINE_COLORS[i]} strokeWidth={1.25} dot={false} connectNulls={false} isAnimationActive={false} />)}
                </ComposedChart>
              )}</StableChartViewport>
            </div>

            {subIndicators.volume && !officialScope && (
              <div className="housing-hts__pane housing-hts__pane--volume">
                <div className="housing-hts__pane-legend"><strong>거래량</strong><span>{activePoint?.volume?.toLocaleString() ?? "-"}</span><span className="housing-hts__legend-note">Strict 가격 gap과 무관하게 연속 · 가격 N/A봉은 중립색</span>{config.volumeMaPeriods.map((period, i) => <span key={period} style={{ color: LINE_COLORS[i] }}>MA{period} {finite(activePoint?.[VOLUME_MA_KEYS[i]]) ? Math.round(Number(activePoint?.[VOLUME_MA_KEYS[i]])).toLocaleString() : "-"}</span>)}</div>
                <StableChartViewport height={150}>{(chartWidth) => (
                  <ComposedChart width={chartWidth} height={150} syncId={SYNC_ID} data={visiblePoints} margin={CHART_MARGIN} onMouseMove={chartMouseMove} onMouseLeave={() => setHoverPoint(null)}>
                    <CartesianGrid strokeDasharray="2 2" vertical={false} opacity={0.12} />
                    {axisBottom(false)}
                    <YAxis orientation="right" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} width={Y_AXIS_WIDTH} />
                    <Tooltip cursor={{ stroke: "#475569", strokeDasharray: "3 3", strokeWidth: 1 }} content={() => null} />
                    {sharedCrosshair()}
                    <Bar dataKey="volume" name="거래량" barSize={adaptiveBarSize(chartWidth, visiblePoints.length)} isAnimationActive={false}>{visiblePoints.map((point, i) => <Cell key={i} fill={pointColor(point)} fillOpacity={0.6} />)}</Bar>
                    {config.volumeMaPeriods.map((period, i) => <Line key={period} type="linear" dataKey={VOLUME_MA_KEYS[i]} name={`Vol MA${period}`} stroke={LINE_COLORS[i]} strokeWidth={1.15} dot={false} connectNulls={false} isAnimationActive={false} />)}
                  </ComposedChart>
                )}</StableChartViewport>
              </div>
            )}

            {subIndicators.macd && (macdHasVisibleValues ? (
              <div className="housing-hts__pane housing-hts__pane--indicator">
                <div className="housing-hts__pane-legend"><strong>MACD({config.macdFast},{config.macdSlow},{config.macdSignal})</strong>{currentSegmentLength < macdRequiredBars ? <span className="is-warn">현재 segment 대기 {currentSegmentLength}/{macdRequiredBars}봉</span> : <><span>MACD {signed(activePoint?.macd, 3)}</span><span>Signal {signed(activePoint?.macdSignal, 3)}</span><span>Osc {signed(activePoint?.macdOsc, 3)}</span></>}</div>
                <StableChartViewport height={150}>{(chartWidth) => (
                  <ComposedChart width={chartWidth} height={150} syncId={SYNC_ID} data={visiblePoints} margin={CHART_MARGIN} onMouseMove={chartMouseMove} onMouseLeave={() => setHoverPoint(null)}>
                    <CartesianGrid strokeDasharray="2 2" vertical={false} opacity={0.12} />
                    {axisBottom(false)}
                    <YAxis orientation="right" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} width={Y_AXIS_WIDTH} domain={["auto", "auto"]} />
                    <Tooltip cursor={{ stroke: "#475569", strokeDasharray: "3 3", strokeWidth: 1 }} content={() => null} />
                    {sharedCrosshair()}
                    <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                    <Bar dataKey="macdOsc" name="Osc" barSize={adaptiveBarSize(chartWidth, visiblePoints.length)} isAnimationActive={false}>{visiblePoints.map((point, i) => <Cell key={i} fill={finite(point.macdOsc) && point.macdOsc >= 0 ? UP_COLOR : DOWN_COLOR} fillOpacity={0.55} />)}</Bar>
                    <Line type="linear" dataKey="macd" name="MACD" stroke="#111827" strokeWidth={1.45} dot={false} connectNulls={false} isAnimationActive={false} />
                    <Line type="linear" dataKey="macdSignal" name="Signal" stroke="#f59e0b" strokeWidth={1.25} dot={false} connectNulls={false} isAnimationActive={false} />
                  </ComposedChart>
                )}</StableChartViewport>
              </div>
            ) : <div className="housing-hts__indicator-compact"><strong>MACD({config.macdFast},{config.macdSlow},{config.macdSignal})</strong><span>현재 표시 범위에 계산 가능한 값 없음 · 현재 segment {currentSegmentLength}/{macdRequiredBars}봉</span></div>)}

            {subIndicators.rsi && (rsiHasVisibleValues ? (
              <div className="housing-hts__pane housing-hts__pane--indicator">
                <div className="housing-hts__pane-legend"><strong>RSI({config.rsiPeriod})</strong><span>{finite(activePoint?.rsi) ? Number(activePoint?.rsi).toFixed(1) : currentSegmentLength < config.rsiPeriod ? `현재 segment 대기 ${currentSegmentLength}/${config.rsiPeriod}봉` : "-"}</span><span>70 / 30 참고선</span></div>
                <StableChartViewport height={135}>{(chartWidth) => (
                  <ComposedChart width={chartWidth} height={135} syncId={SYNC_ID} data={visiblePoints} margin={{ ...CHART_MARGIN, bottom: 4 }} onMouseMove={chartMouseMove} onMouseLeave={() => setHoverPoint(null)}>
                    <CartesianGrid strokeDasharray="2 2" vertical={false} opacity={0.12} />
                    {axisBottom(true)}
                    <YAxis orientation="right" domain={[0, 100]} ticks={[30, 50, 70]} tick={{ fontSize: 9 }} axisLine={false} tickLine={false} width={Y_AXIS_WIDTH} />
                    <Tooltip cursor={{ stroke: "#475569", strokeDasharray: "3 3", strokeWidth: 1 }} content={() => null} />
                    {sharedCrosshair()}
                    <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="3 3" opacity={0.45} />
                    <ReferenceLine y={30} stroke="#2563eb" strokeDasharray="3 3" opacity={0.45} />
                    <Line type="linear" dataKey="rsi" name={`RSI${config.rsiPeriod}`} stroke="#7c3aed" strokeWidth={1.6} dot={false} connectNulls={false} isAnimationActive={false} />
                  </ComposedChart>
                )}</StableChartViewport>
              </div>
            ) : <div className="housing-hts__indicator-compact"><strong>RSI({config.rsiPeriod})</strong><span>현재 표시 범위에 계산 가능한 값 없음 · 현재 segment {currentSegmentLength}/{config.rsiPeriod}봉</span></div>)}

            {!subIndicators.rsi && <div className="housing-hts__xaxis-only"><StableChartViewport height={28}>{(chartWidth) => <ComposedChart width={chartWidth} height={28} syncId={SYNC_ID} data={visiblePoints} margin={{ ...CHART_MARGIN, top: 0 }}>{axisBottom(true)}</ComposedChart>}</StableChartViewport></div>}
            </div>
          </div> : <div className="housing-tech-empty"><strong>{priceBasis === "fixed_cell" ? "Strict Fixed 가격경로 N/A" : priceBasis === "raw_median" ? "Raw observed 가격경로 N/A" : "Quality-adjusted 가격경로 N/A"}</strong><span>{priceBasis === "fixed_cell" ? "선택한 범위·봉에서 공통셀 guard를 통과한 가격봉이 없습니다. 거래가 없다는 뜻은 아니며 Quality-adjusted/Raw observed/Tape는 별도로 확인할 수 있습니다." : priceBasis === "raw_median" ? "선택한 범위·봉에 원거래 중앙가격을 만들 수 있는 거래가 충분하지 않습니다. Quality-adjusted 또는 더 긴 봉을 확인합니다." : "선택한 범위·봉에서 cell-normalized quality guard를 통과한 유효 가격봉이 없습니다. Raw observed와 실거래 Tape에는 원거래가 남아 있을 수 있습니다."}</span>{!officialScope && recommendedResolution && resolution !== recommendedResolution ? <button type="button" className="housing-hts__recommend-button" data-export-hide="true" onClick={() => setResolutionSelection(recommendedResolution)}>권장 {resolutionLabel(recommendedResolution)}로 보기</button> : !officialScope && !recommendedResolution ? <small>현재 자료에는 품질기준을 통과한 대체 봉이 없습니다.</small> : null}</div>}

          <div className="housing-hts__statusbar" data-export-section="statusbar">
            <span><strong>현재 기준</strong> {priceBasisLabel(priceBasis)}</span>
            {!officialScope && <span><strong>봉 품질</strong> QA {Math.round(qualityAdjustedReadyShare * 100)}% · Raw {Math.round(rawReadyShare * 100)}% · Strict {Math.round(fixedReadyShare * 100)}% · Strict 성숙 {Math.round(resolutionQuality.fixedMatureReadyShare * 100)}%</span>}
            {!officialScope && priceBasis === "fixed_cell" && fixedSegmentStarts.length > 0 && <span><strong>Strict 연속성</strong> {fixedSegmentStarts.length}구간 · 100 재기준 {fixedResetCount}회 · gap {fixedGapPoints.length}봉</span>}
            {!officialScope && <span><strong>최근 가격봉 거래</strong> {latestPricePoint?.transactionCount?.toLocaleString() ?? "N/A"}건</span>}
            {!officialScope && resolution !== "1d" && <span><strong>일별 경로 밀도</strong> {Math.round(resolutionQuality.medianValidDayShare * 100)}%</span>}
            {!officialScope && latestReportedDate && <span><strong>최근 신고</strong> {latestReportedDate}</span>}
            <span><strong>화면창</strong> {viewportStartDate ?? "-"} ~ {viewportEndDate ?? "-"} · {viewportSpan}/{rangePoints.length}봉{viewportAtLatest ? " · 최신 포함" : " · 과거 이동"}</span>
            <span><strong>자료범위</strong> {officialScope ? "공식 월간 전체 역사" : `RTMS 완료 ${sourceMonthSegments.length}구간 · 차트 payload 최근 연속 ${data?.coverage.completeMonths.length ?? 0}개월 · retrospective · as-released PIT 아님`}</span>
          </div>

          <section className="housing-hts__series-coverage" data-export-section="series-coverage" aria-label="HTS 시계열 확보 현황">
            <div className="housing-hts__series-coverage-head">
              <div>
                <strong>시계열 확보 현황</strong>
                <span>{SCOPE_LABELS[scope]} · {bandLabel} · {tickMode ? "실거래 Tape" : resolutionLabel(resolution)} · RTMS 전체 완료월 + 현재 HTS payload</span>
              </div>
              <small>RTMS 원자료는 scope별 전체 수집 완료월을 연속구간으로 표시하고, 파생·기술지표는 현재 HTS payload 안의 실제 계산 가능구간을 표시합니다.</small>
            </div>
            <div className="housing-hts__series-coverage-grid">
              {!officialScope && <div className="housing-hts__series-card is-source">
                <span className="housing-hts__series-kind">수집 원자료</span>
                <strong>RTMS 실거래 · {SCOPE_LABELS[scope]}</strong>
                <span>{monthCoverageRangeLabel(sourceMonthSegments)}</span>
                <small>수집 완료 {sourceCompleteMonthCount}개월 · {sourceMonthSegments.length}구간 · 차트 payload 최근 연속 {data?.coverage.completeMonths.length ?? 0}개월 / {data?.coverage.tradeCount.toLocaleString() ?? "0"}건</small>
              </div>}
              {!officialScope && tickMode && <div className="housing-hts__series-card">
                <span className="housing-hts__series-kind">표시 payload</span>
                <strong>실거래 Tape · 현재 가격대</strong>
                <span>{coverageRangeLabel(tapeSeriesCoverage)}</span>
                <small>{tapeSeriesCoverage.validCount.toLocaleString()}건 로드 · Tape용 최근 거래 subset이며 전체 수집건수와 다름</small>
              </div>}
              {officialScope ? <div className="housing-hts__series-card is-source">
                <span className="housing-hts__series-kind">공식 원자료</span>
                <strong>공식 아파트 매매가격지수</strong>
                <span>{coverageRangeLabel(officialSeriesCoverage)}</span>
                <small>{officialSeriesCoverage.validCount}/{officialSeriesCoverage.totalCount} 월봉 · {officialSeriesCoverage.segments.length}구간</small>
              </div> : !tickMode && <>
                <div className="housing-hts__series-card">
                  <span className="housing-hts__series-kind">파생 가격선</span>
                  <strong>Quality-adjusted</strong>
                  <span>{coverageRangeLabel(qaSeriesCoverage)}</span>
                  <small>{qaSeriesCoverage.validCount}/{qaSeriesCoverage.totalCount}봉 유효 · {qaSeriesCoverage.segments.length}구간</small>
                </div>
                <div className="housing-hts__series-card">
                  <span className="housing-hts__series-kind">파생 가격선</span>
                  <strong>Raw observed</strong>
                  <span>{coverageRangeLabel(rawSeriesCoverage)}</span>
                  <small>{rawSeriesCoverage.validCount}/{rawSeriesCoverage.totalCount}봉 유효 · {rawSeriesCoverage.segments.length}구간</small>
                </div>
                <div className="housing-hts__series-card">
                  <span className="housing-hts__series-kind">진단 가격선</span>
                  <strong>Strict Fixed</strong>
                  <span>{coverageRangeLabel(strictSeriesCoverage)}</span>
                  <small>{strictSeriesCoverage.validCount}/{strictSeriesCoverage.totalCount}봉 유효 · {strictSeriesCoverage.segments.length}구간 · gap은 임의 보간하지 않음</small>
                </div>
              </>}
              {!tickMode && <div className="housing-hts__series-card">
                <span className="housing-hts__series-kind">파생 지표</span>
                <strong>거래량</strong>
                <span>{coverageRangeLabel(volumeSeriesCoverage)}</span>
                <small>{volumeSeriesCoverage.validCount}/{volumeSeriesCoverage.totalCount}봉에 거래 존재 · {volumeSeriesCoverage.segments.length}구간</small>
              </div>}
              {!tickMode && priceMaSeriesCoverage.map(({ period, coverage }) => <div key={`price-ma-${period}`} className="housing-hts__series-card">
                <span className="housing-hts__series-kind">기술지표</span>
                <strong>가격 MA{period}</strong>
                <span>{coverageRangeLabel(coverage)}</span>
                <small>{coverage.validCount}/{coverage.totalCount}봉 계산 가능 · {coverage.segments.length}구간 · 현재 가격기준 적용</small>
              </div>)}
              {!tickMode && volumeMaSeriesCoverage.map(({ period, coverage }) => <div key={`volume-ma-${period}`} className="housing-hts__series-card">
                <span className="housing-hts__series-kind">기술지표</span>
                <strong>거래량 MA{period}</strong>
                <span>{coverageRangeLabel(coverage)}</span>
                <small>{coverage.validCount}/{coverage.totalCount}봉 계산 가능 · {coverage.segments.length}구간</small>
              </div>)}
              {!tickMode && <div className="housing-hts__series-card">
                <span className="housing-hts__series-kind">기술지표</span>
                <strong>MACD({config.macdFast},{config.macdSlow},{config.macdSignal})</strong>
                <span>{coverageRangeLabel(macdSeriesCoverage)}</span>
                <small>{macdSeriesCoverage.validCount}/{macdSeriesCoverage.totalCount}봉 계산 가능 · {macdSeriesCoverage.segments.length}구간 · segment reset 반영</small>
              </div>}
              {!tickMode && <div className="housing-hts__series-card">
                <span className="housing-hts__series-kind">기술지표</span>
                <strong>RSI({config.rsiPeriod})</strong>
                <span>{coverageRangeLabel(rsiSeriesCoverage)}</span>
                <small>{rsiSeriesCoverage.validCount}/{rsiSeriesCoverage.totalCount}봉 계산 가능 · {rsiSeriesCoverage.segments.length}구간 · segment reset 반영</small>
              </div>}
            </div>
          </section>
        </>
      )}
    </section>
  );
}


export default function HousingMarketTechnicalChart(props: { officialHousingPrices?: IndicatorSeries[] }) {
  return <HousingHtsErrorBoundary><HousingMarketTechnicalChartInner {...props} /></HousingHtsErrorBoundary>;
}
